import React, { useState, useEffect } from 'react';
import { useUser, useClerk, useSession } from '@clerk/clerk-react';
import { 
  LayoutDashboard, FileText, Settings, LogOut, User, Home,
  CheckCircle, ShieldCheck, Plus, Trash2, ChevronDown, ChevronUp, Search,
  AlertTriangle, Ban, RefreshCw, HardDrive, Wifi, DollarSign, StickyNote,
  Eye, EyeOff, Send, Lock, Unlock, Activity
} from 'lucide-react';

// ── Admin access is controlled via Clerk roles ──────────────────────────────
// In Clerk dashboard → Users → your account → Metadata (public)
// Set: { "company_name": "Spiral Creations", "role": "admin" }
const ADMIN_KEY = 'sc-admin-2026-spiralkey';
// Strip £ and any non-numeric chars before parsing
const parseAmount = (v) => parseFloat(String(v || '0').replace(/[^0-9.]/g, '')) || 0;
// ────────────────────────────────────────────────────────────────────────────


export const ClientDashboard = ({ theme, isDarkMode, onClose, onSignOutSuccess }) => {
  const { user } = useUser();
  const { signOut } = useClerk();
  const { session: currentSession } = useSession();
  const [activeTab, setActiveTab] = useState(() => {
    const hash = window.location.hash.replace('#portal-', '');
    return ['services','invoices','settings','admin'].includes(hash) ? hash : 'services';
  });

  const switchTab = (id) => {
    setActiveTab(id);
    window.history.replaceState(null, '', `#portal-${id}`);
  };

  // Package state — loaded from the database via API
  const [clientPackage, setClientPackage] = useState(null);
  const [packageLoading, setPackageLoading] = useState(true);

  const fetchClientPackage = () => {
    if (!user?.id) return;
    setPackageLoading(true);
    fetch('/api/client-package.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ clerk_user_id: user.id })
    })
      .then(r => r.json())
      .then(data => {
        if (data.success && data.package) setClientPackage(data.package);
        else setClientPackage(null);
      })
      .catch(() => setClientPackage(null))
      .finally(() => setPackageLoading(false));
  };

  // Fetch package on mount and whenever the services or settings tab is opened
  useEffect(() => {
    if (activeTab === 'services' || activeTab === 'settings' || activeTab === 'invoices') fetchClientPackage();
  }, [user?.id, activeTab]);

  const firstName = user?.firstName || user?.primaryEmailAddress?.emailAddress?.split('@')[0] || '';
  const isAdmin = user?.publicMetadata?.role === 'admin';

  // Admin state
  const [adminClients, setAdminClients]       = useState([]);
  const [adminLoading, setAdminLoading]       = useState(false);
  const [adminExpanded, setAdminExpanded]     = useState(null);
  const [adminForm, setAdminForm]             = useState(null);
  const [adminSaving, setAdminSaving]         = useState(false);
  const [adminMsg, setAdminMsg]               = useState(null);
  const [adminSearch, setAdminSearch]         = useState('');
  const [showNewForm, setShowNewForm]         = useState(false);
  const [adminClerkUsers, setAdminClerkUsers] = useState([]);
  const [adminView, setAdminView]             = useState('clients'); // 'clients' | 'invoices'
  const [allInvoices, setAllInvoices]         = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoiceSearch, setInvoiceSearch]     = useState('');

  const EMPTY_PKG = {
    clerk_user_id: '', company_name: '', package_name: 'Web Presence',
    package_tier: 'Essential', since_date: '', renewal_date: '',
    monthly_rolling: false, cpanel_url: '', cpanel_username: '',
    server_location: '', ssl_status: '', uptime_guarantee: '', backup_schedule: '',
    core_services: [{ id: 'domain', label: '', detail: '', paid: true }],
    addons: [{ id: 'addon1', label: '', detail: '', paid: false }],
  };

  const fetchAdminClients = async () => {
    setAdminLoading(true);
    try {
      const [listRes, usersRes] = await Promise.all([
        fetch('/api/admin-package-list.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Admin-Key': ADMIN_KEY },
          body: JSON.stringify({ action: 'list' })
        }),
        fetch('/api/clerk-users.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Admin-Key': ADMIN_KEY },
          body: JSON.stringify({})
        })
      ]);
      const listData  = await listRes.json();
      const usersData = await usersRes.json();
      if (listData.success) setAdminClients(listData.packages.slice().sort((a, b) => (a.company_name || '').localeCompare(b.company_name || '')));
      if (usersData.success) setAdminClerkUsers(usersData.users);
    } catch(e) { console.error(e); }
    finally { setAdminLoading(false); }
  };

  const fetchAllInvoices = async () => {
    setInvoicesLoading(true);
    try {
      const res = await fetch('/api/admin-notes.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Key': ADMIN_KEY },
        body: JSON.stringify({ action: 'list_all', type: 'payment' })
      });
      const data = await res.json();
      if (data.success) setAllInvoices(data.entries);
    } catch(e) { console.error(e); }
    finally { setInvoicesLoading(false); }
  };

  useEffect(() => {
    if (activeTab === 'admin' && isAdmin) {
      fetchAdminClients();
      fetchAllInvoices();
    }
  }, [activeTab, isAdmin]);

  const savePackage = async (pkg) => {
    setAdminSaving(true);
    setAdminMsg(null);
    const cleaned = {
      ...pkg,
      core_services: (pkg.core_services || []).filter(s => s.label?.trim()),
      addons:        (pkg.addons        || []).filter(a => a.label?.trim()),
    };
    try {
      const res = await fetch('/api/admin-package.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Key': ADMIN_KEY },
        body: JSON.stringify(cleaned)
      });
      const data = await res.json();
      if (data.success) {
        setAdminMsg({ text: 'Saved successfully', ok: true });
        setAdminForm(null);
        setShowNewForm(false);
        setAdminExpanded(null);
        fetchAdminClients();
        // If we just saved our own package, refresh the services tab too
        if (cleaned.clerk_user_id === user?.id) fetchClientPackage();
        // Sync company name to Clerk metadata
        if (cleaned.company_name) {
          fetch('/api/clerk-update-meta.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Admin-Key': ADMIN_KEY },
            body: JSON.stringify({ clerk_user_id: cleaned.clerk_user_id, company_name: cleaned.company_name })
          }).catch(() => {}); // fire and forget — DB save already succeeded
        }
      } else {
        setAdminMsg({ text: data.error || 'Error saving', ok: false });
      }
    } catch(e) {
      setAdminMsg({ text: 'Network error', ok: false });
    }
    setAdminSaving(false);
  };

  const [deleteConfirm, setDeleteConfirm] = useState(null); // { clerk_user_id, company_name }
  const [deleteInput, setDeleteInput]     = useState('');

  const deletePackage = (clerk_user_id, company_name) => {
    setDeleteConfirm({ clerk_user_id, company_name });
    setDeleteInput('');
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
      const res = await fetch('/api/admin-package-list.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Key': ADMIN_KEY },
        body: JSON.stringify({ action: 'delete', clerk_user_id: deleteConfirm.clerk_user_id })
      });
      const data = await res.json();
      if (data.success) {
        fetchAdminClients();
        setAdminExpanded(null);
        setAdminForm(null);
      }
    } catch(e) { console.error(e); }
    setDeleteConfirm(null);
    setDeleteInput('');
  };

  const tabs = [
    { id: 'services', label: 'Services',  icon: LayoutDashboard },
    { id: 'invoices', label: 'Invoices',  icon: FileText },
    { id: 'settings', label: 'Account',   icon: User },
    ...(isAdmin ? [{ id: 'admin', label: 'Admin', icon: ShieldCheck }] : []),
  ];

  return (
    <div className="max-w-6xl mx-auto px-6 py-6">

      {/* Header */}
      <div className="mb-6">

        {/* Home + Sign out — top right above everything */}
        <div className="flex justify-end gap-3 mb-4">
          <button
            onClick={() => { onClose(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            className={`flex items-center gap-1.5 text-xs font-sans ${theme.text.muted} hover:text-pink-400 transition-colors`}
          >
            <Home size={13} />
            Home
          </button>
          <button
            onClick={() => { onClose(); signOut().then(() => { if (window.__scSignOutToast) window.__scSignOutToast(); }); }}
            className={`flex items-center gap-1.5 text-xs font-sans ${theme.text.muted} hover:text-red-400 transition-colors`}
          >
            <LogOut size={13} style={{transform: 'scaleX(-1)'}} />
            Sign out
          </button>
        </div>

        <div className="flex items-center gap-2 mb-2">
          {user?.imageUrl
            ? <img src={user.imageUrl} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
            : <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 text-xs font-bold text-white" style={{ background: '#eb008b' }}>{user?.firstName?.[0] ?? '?'}</div>
          }
          <div>
            <p className={`text-xs font-sans tracking-[0.2em] uppercase ${theme.text.muted}`}>Client Portal</p>
            {clientPackage?.companyName && (
              <p className={`text-xs font-sans tracking-[0.15em] uppercase font-medium ${theme.text.secondary} mt-0.5`}>{clientPackage.companyName}</p>
            )}
          </div>
        </div>

        <h1 className={`font-sans font-black uppercase ${theme.text.primary} text-xl sm:text-3xl whitespace-nowrap`} style={{ letterSpacing: '0.05em' }}>
          {(() => {
            const h = new Date().getHours();
            const name = firstName && firstName !== 'there' ? `, ${firstName}` : '';
            if (h >= 0 && h < 4)  return `Working late${name}?`;
            if (h >= 4 && h < 7)  return `Early start${name}!`;
            if (h >= 7 && h < 12) return `Good morning${name}!`;
            if (h >= 12 && h < 17) return `Good afternoon${name}!`;
            return `Good evening${name}!`;
          })()}
        </h1>
        {user?.lastSignInAt && (
          <p className={`${theme.text.muted} text-xs font-sans mt-1`}>
            Last login {new Date(user.lastSignInAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} at {new Date(user.lastSignInAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
          </p>
        )}

        {/* Divider */}
        <div className="h-px mt-6" style={{ background: 'linear-gradient(to right, #eb008b, transparent)' }} />
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-0 mb-8" style={{ borderBottom: isDarkMode ? '1px solid rgba(71,85,105,0.4)' : '1px solid rgba(229,231,235,0.8)' }}>
        {tabs.map(tab => {
          const Icon = tab.icon;
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => switchTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-sans font-medium transition-colors duration-200 relative ${
                active
                  ? isDarkMode ? 'text-white' : 'text-slate-900'
                  : `${theme.text.muted} hover:${isDarkMode ? 'text-white' : 'text-slate-900'}`
              }`}
            >
              <Icon size={13} />
              {tab.label}
              {active && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 rounded-t-full" style={{ background: '#eb008b' }} />
              )}
            </button>
          );
        })}
      </div>

      {/* Services Tab */}
      {activeTab === 'services' && (
        <div className="w-full">
          <div className="w-full">

          {/* Loading state */}
          {packageLoading && (
            <div className={`${theme.card} border rounded-2xl p-12 flex items-center justify-center`}>
              <div className="flex flex-col items-center gap-3">
                <svg className="animate-spin w-6 h-6 text-pink-400" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                </svg>
                <p className={`text-xs font-sans ${theme.text.muted}`}>Loading your package…</p>
              </div>
            </div>
          )}

          {/* No package assigned yet */}
          {!packageLoading && !clientPackage && (
            <div className={`${theme.card} border rounded-2xl overflow-hidden`}>
              <style>{`
                @keyframes floatIcon {
                  0%, 100% { transform: translateY(0px); }
                  50% { transform: translateY(-6px); }
                }
                @keyframes fadeUpIn {
                  from { opacity: 0; transform: translateY(12px); }
                  to   { opacity: 1; transform: translateY(0); }
                }
                .no-pkg-icon  { animation: floatIcon 3s ease-in-out infinite; }
                .no-pkg-row-1 { animation: fadeUpIn 0.4s ease both; }
                .no-pkg-row-2 { animation: fadeUpIn 0.4s ease 0.1s both; }
                .no-pkg-row-3 { animation: fadeUpIn 0.4s ease 0.2s both; }
                .no-pkg-row-4 { animation: fadeUpIn 0.4s ease 0.3s both; }
              `}</style>

              {/* Top accent bar */}
              <div style={{ height: 3, background: 'linear-gradient(to right, #eb008b, transparent)' }} />

              <div className="px-8 py-10 flex flex-col items-center text-center gap-6">

                {/* Avatar */}
                <div className="no-pkg-icon w-16 h-16 rounded-2xl overflow-hidden flex-shrink-0" style={{ border: '2px solid rgba(235,0,139,0.3)', boxShadow: '0 0 20px rgba(235,0,139,0.15)' }}>
                  {user?.imageUrl
                    ? <img src={user.imageUrl} alt="" className="w-full h-full object-cover" />
                    : <div className="w-full h-full flex items-center justify-center" style={{ background: 'rgba(235,0,139,0.08)' }}>
                        <span className="font-sans font-bold text-xl" style={{ color: '#eb008b' }}>
                          {user?.firstName?.[0] ?? '?'}
                        </span>
                      </div>
                  }
                </div>

                {/* Heading */}
                <div className="no-pkg-row-1">
                  <p className={`font-sans font-black uppercase text-lg ${theme.text.primary}`} style={{ letterSpacing: '0.05em' }}>No package assigned yet</p>
                  <p className={`text-xs font-sans ${theme.text.muted} mt-1`}>Your account is set up — we just need to configure your services.</p>
                </div>

                {/* What will appear here */}
                <div className={`no-pkg-row-2 w-full rounded-xl p-5 text-left space-y-3`} style={{ background: isDarkMode ? 'rgba(15,23,42,0.4)' : 'rgba(248,250,252,0.8)', border: isDarkMode ? '1px solid rgba(71,85,105,0.3)' : '1px solid rgba(229,231,235,0.8)' }}>
                  <p className={`text-[10px] font-sans tracking-[0.2em] uppercase ${theme.text.muted} mb-3`}>What you'll see here</p>
                  {[
                    ['Your active services', 'Domain, hosting, and any other core services'],
                    ['Included add-ons', 'Maintenance, security updates, and more'],
                    ['Renewal information', 'Package dates and review schedule'],
                  ].map(([title, desc], i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-1 h-1 rounded-full mt-2 flex-shrink-0" style={{ background: '#eb008b' }} />
                      <div>
                        <p className={`text-xs font-sans font-medium ${theme.text.secondary}`}>{title}</p>
                        <p className={`text-[11px] font-sans ${theme.text.muted}`}>{desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* CTA */}
                <div className="no-pkg-row-3 flex flex-col items-center gap-2">
                  <button
                    onClick={() => { if (typeof onClose === 'function') onClose(); setTimeout(() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 500); }}
                    className="text-sm font-sans font-medium px-6 py-2.5 rounded-lg text-white transition-opacity hover:opacity-90"
                    style={{ background: '#eb008b' }}
                  >
                    Get in touch
                  </button>
                  <p className={`text-[11px] font-sans ${theme.text.muted}`}>We'll have you set up in no time.</p>
                </div>

              </div>
            </div>
          )}

          {/* Package card */}
          {!packageLoading && clientPackage && (
          <div className={`${theme.card} border rounded-2xl overflow-hidden relative`}>

            {/* Status tab — top left */}
            <div style={{ position: 'absolute', top: 0, left: 28, zIndex: 1 }}>
              <div className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-sans font-semibold tracking-[0.15em] uppercase" style={{ background: isDarkMode ? 'rgba(71,85,105,0.8)' : '#1e293b', borderRadius: '0 0 8px 8px', color: 'white' }}>
                <span style={{ position: 'relative', width: 6, height: 6, flexShrink: 0, display: 'inline-block' }}>
                  <span style={{ position: 'absolute', inset: 0, borderRadius: '50%', backgroundColor: '#4ade80', animation: 'pulseRing 2s ease-out infinite', opacity: 0 }} />
                  <span style={{ position: 'absolute', inset: 0, borderRadius: '50%', backgroundColor: '#4ade80', animation: 'pulseRing 2s ease-out infinite', animationDelay: '1s', opacity: 0 }} />
                  <span style={{ position: 'absolute', inset: 0, borderRadius: '50%', backgroundColor: '#4ade80' }} />
                </span>
                Active Package
              </div>
            </div>

            {/* Company name tab — top right */}
            {clientPackage.companyName && (
              <div style={{ position: 'absolute', top: 0, right: 28, zIndex: 1 }}>
                <div
                  className="flex items-center px-4 py-1.5 text-white text-[10px] font-sans font-semibold tracking-[0.18em] uppercase"
                  style={{ background: '#eb008b', borderRadius: '0 0 8px 8px', boxShadow: '0 2px 10px rgba(235,0,139,0.3)' }}
                >
                  {clientPackage.companyName}
                </div>
              </div>
            )}

            {/* Card header — package identity */}
            <div className="px-8 pt-10 pb-6" style={{ borderBottom: isDarkMode ? '1px solid rgba(71,85,105,0.3)' : '1px solid rgba(229,231,235,0.8)' }}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className={`font-sans font-black text-2xl uppercase ${theme.text.primary}`} style={{ letterSpacing: '0.04em' }}>
                    {clientPackage.name}
                  </h2>
                  {clientPackage.since && (
                    <span className={`inline-flex items-center text-[10px] font-sans px-2.5 py-1 rounded-full mt-2 ${isDarkMode ? 'bg-slate-700/50 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                      Since {clientPackage.since}
                    </span>
                  )}
                </div>
                {(() => {
                  const allItems = [
                    ...(clientPackage.coreServices || []).filter(s => s.paid !== false && s.price),
                    ...(clientPackage.addons || []).filter(a => a.paid && a.price),
                  ];
                  const monthly = allItems.reduce((sum, item) => {
                    const net = Math.max(0, (parseFloat(item.price)||0) - (parseFloat(item.discount)||0));
                    if ((item.period||'month') === 'month')   return sum + net;
                    if ((item.period||'month') === 'quarter') return sum + net / 3;
                    if ((item.period||'month') === 'week')    return sum + net * 4.33;
                    return sum;
                  }, 0);
                  return monthly > 0 ? (
                    <div className="text-right flex-shrink-0">
                      <p className={`text-[9px] font-sans tracking-[0.15em] uppercase ${theme.text.muted} mb-0.5`}>Monthly</p>
                      <p className={`font-sans font-black text-xl ${theme.text.primary}`}>
                        £{monthly.toFixed(2)}
                        <span className={`text-[10px] font-normal ${theme.text.muted} ml-1`}>/mo</span>
                      </p>
                    </div>
                  ) : null;
                })()}
              </div>
            </div>

            {/* Core services */}
            {clientPackage.coreServices?.length > 0 && (
            <div className="px-8 py-6">
              <div className="inline-flex items-center px-4 py-1 mb-4 text-white text-[10px] font-sans font-semibold tracking-[0.2em] uppercase" style={{ background: '#eb008b', borderRadius: '0 6px 6px 0', marginLeft: '-2rem', paddingLeft: '2rem' }}>Core Services</div>
              <div className="space-y-0">
                {clientPackage.coreServices.map((svc, i) => {
                  const periodLabel = (svc.period || 'month') === 'month' ? 'mo' : (svc.period || 'month') === 'year' ? 'yr' : (svc.period || 'month') === 'quarter' ? 'qtr' : (svc.period || 'month') === 'week' ? 'wk' : 'one-off';
                  const hasDiscount = svc.discount && svc.discountLabel;
                  const finalPrice = hasDiscount ? (parseFloat(svc.price) - parseFloat(svc.discount)).toFixed(2) : null;
                  const lines = svc.detail ? svc.detail.split('\n') : [];
                  return (
                  <div
                    key={svc.id || i}
                    className="py-3"
                    style={i < clientPackage.coreServices.length - 1 ? { borderBottom: isDarkMode ? '1px solid rgba(71,85,105,0.3)' : '1px solid rgba(229,231,235,0.8)' } : {}}
                  >
                    {/* Title row with price */}
                    <div className="flex items-center gap-3">
                      <CheckCircle size={13} className="text-green-400 flex-shrink-0" />
                      <p className={`font-sans text-sm font-medium flex-1 ${theme.text.primary}`}>{svc.label}</p>
                      {svc.paid === false ? (
                        <span className="flex-shrink-0 text-[10px] font-sans tracking-wide text-green-400 border border-green-400/30 font-bold rounded px-1.5 py-0.5">
                          FREE
                        </span>
                      ) : svc.price ? (
                        <div className="flex-shrink-0 text-right">
                          {hasDiscount ? (
                            <div className="flex items-baseline gap-1.5 justify-end flex-wrap">
                              <span className={`text-[10px] font-sans line-through ${theme.text.muted}`}>£{parseFloat(svc.price).toFixed(2)}</span>
                              <span className="text-sm font-sans font-bold text-green-400">£{finalPrice}</span>
                              <span className={`text-[10px] font-sans ${theme.text.muted}`}>/ {periodLabel}</span>
                            </div>
                          ) : (
                            <>
                              <span className={`text-sm font-sans font-bold ${theme.text.primary}`}>£{parseFloat(svc.price).toFixed(2)}</span>
                              <span className={`text-[10px] font-sans ${theme.text.muted}`}> / {periodLabel}</span>
                            </>
                          )}
                        </div>
                      ) : null}
                    </div>
                    {/* Description — full width below */}
                    <div className="pl-5 mt-0.5">
                      {lines.map((line, li) => /^[-•*] /.test(line) ? (
                        <div key={li} className={`font-sans text-xs mt-0.5 ${theme.text.muted} flex items-center gap-1.5`}>
                          <span className="w-1 h-1 rounded-full flex-shrink-0" style={{ background: 'currentColor', opacity: 0.5 }} />
                          <span>{line.slice(2)}</span>
                        </div>
                      ) : line ? (
                        <p key={li} className={`font-sans text-xs mt-0.5 ${theme.text.muted}`}>{line}</p>
                      ) : null)}
                      {hasDiscount && (
                        <p className="text-[10px] font-sans text-green-400 mt-1">{svc.discountLabel}</p>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
            )}

            {/* Add-ons */}
            <div className="px-8 py-6" style={{ borderTop: isDarkMode ? '1px solid rgba(71,85,105,0.3)' : '1px solid rgba(229,231,235,0.8)' }}>
              <div className="inline-flex items-center px-4 py-1 mb-4 text-white text-[10px] font-sans font-semibold tracking-[0.2em] uppercase" style={{ background: '#eb008b', borderRadius: '0 6px 6px 0', marginLeft: '-2rem', paddingLeft: '2rem' }}>Included Add-ons</div>
              {clientPackage.addons?.length > 0 ? (
              <div className="space-y-0">
                {clientPackage.addons.map((addon, i) => {
                  const periodLabel = (addon.period || 'month') === 'month' ? 'mo' : (addon.period || 'month') === 'year' ? 'yr' : (addon.period || 'month') === 'quarter' ? 'qtr' : (addon.period || 'month') === 'week' ? 'wk' : 'one-off';
                  const hasDiscount = addon.discount && addon.discountLabel;
                  const finalPrice = hasDiscount ? (parseFloat(addon.price) - parseFloat(addon.discount)).toFixed(2) : null;
                  const lines = addon.detail ? addon.detail.split('\n') : [];
                  return (
                  <div
                    key={addon.id || i}
                    className="py-3"
                    style={i < clientPackage.addons.length - 1 ? { borderBottom: isDarkMode ? '1px solid rgba(71,85,105,0.2)' : '1px solid rgba(229,231,235,0.6)' } : {}}
                  >
                    {/* Title row with price */}
                    <div className="flex items-center gap-3">
                      <CheckCircle size={13} className="text-green-400 flex-shrink-0" />
                      <p className={`font-sans text-sm font-medium flex-1 ${theme.text.primary}`}>{addon.label}</p>
                      {addon.paid && addon.price ? (
                        <div className="flex-shrink-0 text-right">
                          {hasDiscount ? (
                            <div className="flex items-baseline gap-1.5 justify-end flex-wrap">
                              <span className={`text-[10px] font-sans line-through ${theme.text.muted}`}>£{parseFloat(addon.price).toFixed(2)}</span>
                              <span className="text-sm font-sans font-bold text-green-400">£{finalPrice}</span>
                              <span className={`text-[10px] font-sans ${theme.text.muted}`}>/ {periodLabel}</span>
                            </div>
                          ) : (
                            <>
                              <span className={`text-sm font-sans font-bold ${theme.text.primary}`}>£{parseFloat(addon.price).toFixed(2)}</span>
                              <span className={`text-[10px] font-sans ${theme.text.muted}`}> / {periodLabel}</span>
                            </>
                          )}
                        </div>
                      ) : !addon.paid ? (
                        <span className="flex-shrink-0 text-[10px] font-sans tracking-wide text-green-400 border border-green-400/30 font-bold rounded px-1.5 py-0.5">
                          FREE
                        </span>
                      ) : null}
                    </div>
                    {/* Description — full width below */}
                    <div className="pl-5 mt-0.5">
                      {lines.map((line, li) => /^[-•*] /.test(line) ? (
                        <div key={li} className={`font-sans text-xs mt-0.5 ${theme.text.muted} flex items-center gap-1.5`}>
                          <span className="w-1 h-1 rounded-full flex-shrink-0" style={{ background: 'currentColor', opacity: 0.5 }} />
                          <span>{line.slice(2)}</span>
                        </div>
                      ) : line ? (
                        <p key={li} className={`font-sans text-xs mt-0.5 ${theme.text.muted}`}>{line}</p>
                      ) : null)}
                      {hasDiscount && (
                        <p className="text-[10px] font-sans text-green-400 mt-1">{addon.discountLabel}</p>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
              ) : (
                <p className={`text-xs font-sans ${theme.text.muted}`}>
                  No add-ons on your current package. <button onClick={() => { if (typeof onClose === 'function') onClose(); setTimeout(() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 500); }} className="text-pink-400 hover:text-pink-300 transition-colors">Contact us</button> to find out what's available.
                </p>
              )}
            </div>

            {/* cPanel access */}
            {(clientPackage.cpanelUrl || clientPackage.cpanel_url) && (
              <div className="px-8 py-4" style={{ borderTop: isDarkMode ? '1px solid rgba(71,85,105,0.3)' : '1px solid rgba(229,231,235,0.8)' }}>
                <div className="inline-flex items-center px-4 py-1 mb-4 text-white text-[10px] font-sans font-semibold tracking-[0.2em] uppercase" style={{ background: '#eb008b', borderRadius: '0 6px 6px 0', marginLeft: '-2rem', paddingLeft: '2rem' }}>Server Access</div>
                <div className="flex items-center justify-between">
                  {(clientPackage.cpanelUsername || clientPackage.cpanel_username) && (
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: isDarkMode ? 'rgba(71,85,105,0.5)' : '#1e293b', border: isDarkMode ? '1px solid rgba(71,85,105,0.6)' : '1px solid #0f172a' }}>
                        <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/>
                        </svg>
                      </div>
                      <div>
                        <p className={`text-[10px] font-sans ${theme.text.muted} leading-none mb-0.5`}>cPanel Account</p>
                        <p className={`text-xs font-sans font-medium font-mono ${theme.text.secondary}`}>{clientPackage.cpanelUsername || clientPackage.cpanel_username}</p>
                      </div>
                    </div>
                  )}
                  <button
                    onClick={async () => {
                      const username = clientPackage.cpanelUsername || clientPackage.cpanel_username;
                      if (!username) {
                        window.open(clientPackage.cpanelUrl || clientPackage.cpanel_url, '_blank');
                        return;
                      }
                      try {
                        const res = await fetch('/api/cpanel-login.php', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', 'X-Admin-Key': ADMIN_KEY },
                          body: JSON.stringify({ cpanel_username: username })
                        });
                        const data = await res.json();
                        if (data.success && data.url) {
                          window.open(data.url, '_blank');
                        } else {
                          window.open(clientPackage.cpanelUrl || clientPackage.cpanel_url, '_blank');
                        }
                      } catch {
                        window.open(clientPackage.cpanelUrl || clientPackage.cpanel_url, '_blank');
                      }
                    }}
                    className="inline-flex items-center gap-2.5 text-xs font-sans font-semibold px-4 py-2.5 rounded-lg transition-opacity hover:opacity-90 cursor-pointer"
                    style={{ background: isDarkMode ? 'rgba(71,85,105,0.5)' : '#1e293b', border: isDarkMode ? '1px solid rgba(71,85,105,0.6)' : '1px solid #0f172a' }}
                  >
                    <img src="/cpanel.webp" alt="cPanel" style={{ height: 16, width: 'auto' }} />
                    <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 10 }}>|</span>
                    <span className="text-white text-[11px] font-medium">Launch Control Panel</span>
                    <svg width="11" height="11" fill="none" viewBox="0 0 24 24" stroke="rgba(255,255,255,0.5)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {/* Live hosting stats */}
            {(clientPackage.cpanelUsername || clientPackage.cpanel_username) && (
              <HostingStats
                cpanelUsername={clientPackage.cpanelUsername || clientPackage.cpanel_username}
                isDarkMode={isDarkMode}
                theme={theme}
              />
            )}

            {/* Card footer — renewal */}
            <div
              className="px-8 py-4 flex items-center justify-between gap-4"
              style={{ borderTop: isDarkMode ? '1px solid rgba(71,85,105,0.3)' : '1px solid rgba(229,231,235,0.8)', background: isDarkMode ? 'rgba(15,23,42,0.3)' : 'rgba(248,250,252,0.6)' }}
            >
              <div className="flex flex-col">
                {clientPackage.monthlyRolling || clientPackage.renewalDate === 'Monthly rolling'
                  ? (() => {
                      const since = clientPackage.since;
                      const dayMatch = since ? since.match(/(\d+)/) : null;
                      const day = dayMatch ? parseInt(dayMatch[1]) : null;
                      const suffix = day ? (day === 1 ? 'st' : day === 2 ? 'nd' : day === 3 ? 'rd' : 'th') : '';
                      return <>
                        <span className={`text-xs font-sans font-medium ${theme.text.secondary}`}>Monthly rolling</span>
                        {day && <span className={`text-[10px] font-sans ${theme.text.muted}`}>{day}{suffix} of each month</span>}
                      </>;
                    })()
                  : <>
                      <span className={`text-[10px] font-sans ${theme.text.muted}`}>Renews</span>
                      <span className={`text-xs font-sans font-medium ${theme.text.secondary}`}>{clientPackage.renewalDate}</span>
                    </>
                }
              </div>
              <button
                onClick={() => { if (typeof onClose === 'function') onClose(); setTimeout(() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 500); }}
                className="flex-shrink-0 text-xs font-sans font-medium text-white px-4 py-2 rounded-lg transition-opacity hover:opacity-90"
                style={{ background: '#eb008b' }}
              >
                Discuss your package →
              </button>
            </div>

          </div>
          )}
        </div>
        </div>
      )}

      {/* Invoices Tab */}
      {activeTab === 'invoices' && (
        <ClientInvoicesTab
          clientPackage={clientPackage}
          packageLoading={packageLoading}
          userId={user?.id}
          isDarkMode={isDarkMode}
          theme={theme}
          onClose={onClose}
        />
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="w-full">
          <div className={`${theme.card} border rounded-2xl overflow-hidden`}>
            <div style={{ height: 3, background: 'linear-gradient(to right, #eb008b, transparent)' }} />
            <div className="p-6 flex items-start gap-6">

              {/* Left — user details */}
              <div className="flex-1 min-w-0 space-y-4">
                {clientPackage?.companyName && (
                  <div>
                    <p className={`text-[10px] font-sans tracking-[0.15em] uppercase ${theme.text.muted} mb-0.5`}>Company</p>
                    <p className={`text-sm font-sans font-semibold ${theme.text.primary} truncate`}>{clientPackage.companyName}</p>
                  </div>
                )}
                <div>
                  <p className={`text-[10px] font-sans tracking-[0.15em] uppercase ${theme.text.muted} mb-0.5`}>Full Name</p>
                  <p className={`text-sm font-sans font-medium ${theme.text.primary}`}>{user?.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : '—'}</p>
                </div>
                <div>
                  <p className={`text-[10px] font-sans tracking-[0.15em] uppercase ${theme.text.muted} mb-0.5`}>Email</p>
                  <p className={`text-sm font-sans ${theme.text.secondary} truncate`}>{user?.primaryEmailAddress?.emailAddress}</p>
                </div>
                <div>
                  <p className={`text-[10px] font-sans tracking-[0.15em] uppercase ${theme.text.muted} mb-0.5`}>Member Since</p>
                  <p className={`text-sm font-sans ${theme.text.secondary}`}>{user?.createdAt ? new Date(user.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}</p>
                </div>
                <div>
                  <p className={`text-[10px] font-sans tracking-[0.15em] uppercase ${theme.text.muted} mb-0.5`}>Last Login</p>
                  <p className={`text-sm font-sans ${theme.text.secondary}`}>{user?.lastSignInAt ? `${new Date(user.lastSignInAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} at ${new Date(user.lastSignInAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}` : '—'}</p>
                </div>
                <p className={`text-[11px] font-sans ${theme.text.muted} pt-1`}>
                  Need to update your details? <button onClick={() => { onClose(); setTimeout(() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 500); }} className="text-pink-400 hover:text-pink-300 transition-colors">Contact us</button>
                </p>
              </div>

              {/* Right — large avatar with pen */}
              <AvatarUpload user={user} isDarkMode={isDarkMode} theme={theme} size="lg" />

            </div>
          </div>

          {/* Active sessions */}
          <ActiveSessions user={user} currentSessionId={currentSession?.id} isDarkMode={isDarkMode} theme={theme} />
        </div>
      )}

      {/* Admin Tab */}
      {activeTab === 'admin' && isAdmin && (
        <div>

          {/* Stats bar */}
          {!adminLoading && adminClients.length > 0 && (() => {
            const totalMRR = adminClients.reduce((sum, c) => {
              const items = [...(c.core_services||[]).filter(s=>s.paid!==false&&s.price), ...(c.addons||[]).filter(a=>a.paid&&a.price)];
              return sum + items.reduce((s, item) => {
                const net = Math.max(0,(parseFloat(item.price)||0)-(parseFloat(item.discount)||0));
                if ((item.period||'month')==='month')   return s+net;
                if ((item.period||'month')==='quarter') return s+net/3;
                if ((item.period||'month')==='week')    return s+net*4.33;
                return s;
              }, 0);
            }, 0);
            const unpaid  = allInvoices.filter(i=>i.status==='unpaid').length;
            const overdue = allInvoices.filter(i=>i.status==='overdue').length;
            const stats = [
              { label: 'Clients',    value: adminClients.length, colour: '#eb008b' },
              { label: 'MRR',        value: `£${totalMRR.toFixed(2)}`, colour: '#4ade80' },
              { label: 'Unpaid',     value: unpaid,  colour: '#fbbf24' },
              { label: 'Overdue',    value: overdue, colour: '#f87171' },
            ];
            return (
              <div className="grid grid-cols-4 gap-2 mb-6">
                {stats.map(s => (
                  <div key={s.label} className={`${theme.card} border rounded-xl px-3 py-3`}>
                    <p className={`text-[8px] font-sans tracking-[0.12em] uppercase ${theme.text.muted} mb-0.5 truncate`}>{s.label}</p>
                    <p className="text-base font-sans font-black leading-none" style={{ color: s.colour }}>{s.value}</p>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* View toggle tabs */}
          <div className="flex mb-5" style={{ borderBottom: isDarkMode ? '1px solid rgba(71,85,105,0.4)' : '1px solid rgba(229,231,235,0.8)' }}>
            {[
              { id: 'clients',  label: 'Clients' },
              { id: 'invoices', label: 'All Invoices' },
            ].map(v => {
              const active = adminView === v.id;
              return (
                <button
                  key={v.id}
                  onClick={() => setAdminView(v.id)}
                  className={`px-4 py-2.5 text-xs font-sans font-medium transition-colors relative ${active ? (isDarkMode ? 'text-white' : 'text-slate-900') : theme.text.muted}`}
                >
                  {v.label}
                  {active && <span className="absolute bottom-0 left-0 right-0 h-0.5 rounded-t-full" style={{ background: '#eb008b' }} />}
                </button>
              );
            })}
            {/* New client button stays top right */}
            {adminView === 'clients' && (
              <button
                onClick={() => { setAdminForm({ ...EMPTY_PKG }); setShowNewForm(true); setAdminExpanded(null); }}
                className="flex items-center gap-1.5 text-xs font-sans font-medium text-white px-3 py-2 rounded-lg transition-opacity hover:opacity-90 ml-auto mb-1"
                style={{ background: '#eb008b' }}
              >
                <Plus size={13} /> New client
              </button>
            )}
          </div>

          {/* Success / error message */}
          {adminMsg && (
            <div className={`text-xs font-sans px-4 py-3 rounded-lg mb-4 ${adminMsg.ok ? 'text-green-400 bg-green-400/10 border border-green-400/20' : 'text-red-400 bg-red-400/10 border border-red-400/20'}`}>
              {adminMsg.text}
            </div>
          )}

          {/* ── Clients view ── */}
          {adminView === 'clients' && (
            <div>
              {/* New client form */}
              {showNewForm && adminForm && (
                <AdminPackageForm
                  pkg={adminForm}
                  onChange={setAdminForm}
                  onSave={() => savePackage(adminForm)}
                  onCancel={() => { setShowNewForm(false); setAdminForm(null); setAdminMsg(null); }}
                  saving={adminSaving}
                  isDarkMode={isDarkMode}
                  theme={theme}
                  isNew
                  existingIds={adminClients.map(c => c.clerk_user_id)}
                />
              )}

              {/* Search */}
              {adminClients.length > 0 && (
                <div className="relative mb-4">
                  <Search size={13} className={`absolute left-3 top-1/2 -translate-y-1/2 ${theme.text.muted}`} />
                  <input
                    type="text"
                    placeholder="Search clients…"
                    value={adminSearch}
                    onChange={e => setAdminSearch(e.target.value)}
                    className={`w-full ${theme.input} rounded-lg pl-8 pr-4 py-2.5 text-xs font-sans`}
                  />
                </div>
              )}

              {/* Client list */}
              {adminLoading ? (
                <div className="flex justify-center py-12">
                  <svg className="animate-spin w-5 h-5 text-pink-400" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                  </svg>
                </div>
              ) : (
                <div className="space-y-2">
                  {adminClients
                    .filter(c => {
                      const clerkUser = adminClerkUsers.find(u => u.id === c.clerk_user_id);
                      const searchStr = [c.company_name, clerkUser?.name, clerkUser?.email, c.clerk_user_id].filter(Boolean).join(' ').toLowerCase();
                      return !adminSearch || searchStr.includes(adminSearch.toLowerCase());
                    })
                    .map(client => {
                      const expanded  = adminExpanded === client.clerk_user_id;
                      const clerkUser = adminClerkUsers.find(u => u.id === client.clerk_user_id);
                      return (
                        <div key={client.clerk_user_id} className={`${theme.card} border rounded-xl overflow-hidden transition-all`}>
                          {/* Row header */}
                          <div className="flex items-center justify-between px-5 py-4 cursor-pointer" onClick={() => {
                            if (expanded) { setAdminExpanded(null); setAdminForm(null); }
                            else { setAdminExpanded(client.clerk_user_id); setAdminForm({ ...client }); setShowNewForm(false); }
                          }}>
                            <div className="flex items-center gap-3 min-w-0 flex-1">
                              <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: '#4ade80' }} />
                              <div className="min-w-0">
                                <p className={`font-sans font-semibold text-sm ${theme.text.primary} truncate`}>{client.company_name || '—'}</p>
                                <p className={`text-[11px] font-sans ${theme.text.muted} truncate`}>{client.package_name} · {client.monthly_rolling ? 'Monthly rolling' : `renews ${client.renewal_date || '—'}`}</p>
                              </div>
                            </div>
                            {clerkUser && (
                              <div className="flex items-center gap-2 mx-4 flex-shrink-0">
                                {clerkUser.avatar
                                  ? <img src={clerkUser.avatar} alt="" className="w-6 h-6 rounded-lg object-cover flex-shrink-0" />
                                  : <div className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 text-[10px] font-bold text-white" style={{ background: '#eb008b' }}>{(clerkUser.name || clerkUser.email)[0].toUpperCase()}</div>
                                }
                                <div className="hidden sm:block text-right">
                                  {clerkUser.name && <p className={`text-[11px] font-sans font-medium ${theme.text.secondary} truncate max-w-[140px]`}>{clerkUser.name}</p>}
                                  <p className={`text-[10px] font-sans ${theme.text.muted} truncate max-w-[140px]`}>{clerkUser.email}</p>
                                </div>
                              </div>
                            )}
                            <div className="flex items-center gap-3 flex-shrink-0">
                              <button
                                onClick={e => { e.stopPropagation(); deletePackage(client.clerk_user_id, client.company_name); }}
                                className={`${theme.text.muted} hover:text-red-400 transition-colors`}
                              >
                                <Trash2 size={13} />
                              </button>
                              {expanded ? <ChevronUp size={14} className={theme.text.muted} /> : <ChevronDown size={14} className={theme.text.muted} />}
                            </div>
                          </div>

                          {/* Expanded — tabbed client management */}
                          {expanded && (
                            <div className={`border-t ${isDarkMode ? 'border-slate-700/50' : 'border-gray-200'}`}>
                              <ClientManageDrawer
                                client={client}
                                clerkUser={clerkUser}
                                adminForm={adminForm}
                                setAdminForm={setAdminForm}
                                onSave={() => savePackage(adminForm)}
                                onClose={() => { setAdminExpanded(null); setAdminForm(null); setAdminMsg(null); }}
                                saving={adminSaving}
                                isDarkMode={isDarkMode}
                                theme={theme}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  {adminClients.length === 0 && !showNewForm && (
                    <p className={`text-xs font-sans ${theme.text.muted} text-center py-10`}>No clients yet. Click <strong>New client</strong> to add one.</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── All Invoices view ── */}
          {adminView === 'invoices' && (
            <div>
              {/* Search */}
              <div className="relative mb-4">
                <Search size={13} className={`absolute left-3 top-1/2 -translate-y-1/2 ${theme.text.muted}`} />
                <input
                  type="text"
                  placeholder="Search by company or description…"
                  value={invoiceSearch}
                  onChange={e => setInvoiceSearch(e.target.value)}
                  className={`w-full ${theme.input} rounded-lg pl-8 pr-4 py-2.5 text-xs font-sans`}
                />
              </div>

              {invoicesLoading ? (
                <div className="flex justify-center py-12">
                  <svg className="animate-spin w-5 h-5 text-pink-400" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                  </svg>
                </div>
              ) : (
                <div className="space-y-2">
                  {/* Auto-generated invoices (one per client) */}
                  {adminClients
                    .filter(c => {
                      const pkg = c;
                      const items = [...(pkg.core_services||[]).filter(s=>s.paid!==false&&s.price), ...(pkg.addons||[]).filter(a=>a.paid&&a.price)];
                      const hasPrice = items.length > 0;
                      const search = invoiceSearch.toLowerCase();
                      return hasPrice && (!search || (c.company_name||'').toLowerCase().includes(search) || (c.package_name||'').toLowerCase().includes(search));
                    })
                    .map(c => {
                      const d = new Date();
                      const num = `INV-${d.getFullYear()}${String(d.getMonth()+1).padStart(2,'0')}-001`;
                      const items = [...(c.core_services||[]).filter(s=>s.paid!==false&&s.price), ...(c.addons||[]).filter(a=>a.paid&&a.price)];
                      const monthly = items.reduce((s,item) => {
                        const net = Math.max(0,(parseFloat(item.price)||0)-(parseFloat(item.discount)||0));
                        if ((item.period||'month')==='month') return s+net;
                        if ((item.period||'month')==='quarter') return s+net/3;
                        if ((item.period||'month')==='week') return s+net*4.33;
                        return s;
                      }, 0);
                      return (
                        <div key={`auto-${c.clerk_user_id}`} className={`${theme.card} border rounded-xl px-5 py-3 flex items-center justify-between gap-4`}>
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: '#eb008b' }} />
                            <div className="min-w-0">
                              <p className={`text-xs font-sans font-semibold ${theme.text.primary} truncate`}>{c.company_name}</p>
                              <p className={`text-[10px] font-sans ${theme.text.muted}`}>{num} · Auto-generated</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0">
                            <span className={`text-xs font-sans font-bold ${theme.text.primary}`}>£{monthly.toFixed(2)}<span className={`text-[9px] font-normal ${theme.text.muted} ml-0.5`}>/mo</span></span>
                            <span className="text-[9px] font-sans font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded border text-yellow-400 bg-yellow-400/10 border-yellow-400/20">Recurring</span>
                            <a href={`/api/invoice-pdf.php?type=auto&clerk_user_id=${c.clerk_user_id}&admin_key=${ADMIN_KEY}`} target="_blank" rel="noreferrer" className={`text-[10px] font-sans ${theme.text.muted} hover:text-pink-400 transition-colors`}>PDF</a>
                          </div>
                        </div>
                      );
                    })}

                  {/* Custom invoices */}
                  {allInvoices
                    .filter(e => {
                      const search = invoiceSearch.toLowerCase();
                      const client = adminClients.find(c => c.clerk_user_id === e.clerk_user_id);
                      const company = client?.company_name || '';
                      return !search || company.toLowerCase().includes(search) || (e.description||'').toLowerCase().includes(search);
                    })
                    .map(e => {
                      const client = adminClients.find(c => c.clerk_user_id === e.clerk_user_id);
                      const statusCls = e.status==='paid' ? 'text-green-400 bg-green-400/10 border-green-400/20' : e.status==='overdue' ? 'text-red-400 bg-red-400/10 border-red-400/20' : 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20';
                      const d = new Date(e.created_at || Date.now());
                      const num = `INV-${d.getFullYear()}${String(d.getMonth()+1).padStart(2,'0')}-${String(e.id).padStart(3,'0')}`;
                      return (
                        <div key={`custom-${e.id}`} className={`${theme.card} border rounded-xl px-5 py-3 flex items-center justify-between gap-4`}>
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            <div className="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-blue-400" />
                            <div className="min-w-0">
                              <p className={`text-xs font-sans font-semibold ${theme.text.primary} truncate`}>{client?.company_name || e.clerk_user_id}</p>
                              <p className={`text-[10px] font-sans ${theme.text.muted} truncate`}>{num} · {e.description}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0">
                            <span className={`text-xs font-sans font-bold ${theme.text.primary}`}>£{parseAmount(e.amount).toFixed(2)}</span>
                            <span className={`text-[9px] font-sans font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded border ${statusCls}`}>{e.status}</span>
                            {e.due_date && <span className={`text-[10px] font-sans ${theme.text.muted} hidden sm:inline`}>Due {new Date(e.due_date).toLocaleDateString('en-GB',{day:'numeric',month:'short'})}</span>}
                            <a href={`/api/invoice-pdf.php?type=custom&id=${e.id}&admin_key=${ADMIN_KEY}`} target="_blank" rel="noreferrer" className={`text-[10px] font-sans ${theme.text.muted} hover:text-pink-400 transition-colors`}>PDF</a>
                          </div>
                        </div>
                      );
                    })}

                  {allInvoices.length === 0 && adminClients.length === 0 && (
                    <p className={`text-xs font-sans ${theme.text.muted} text-center py-10`}>No invoices yet.</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' }}>
          <div className={`${isDarkMode ? 'bg-slate-800/90' : 'bg-gray-100/95'} backdrop-blur-sm border ${isDarkMode ? 'border-slate-700/50' : 'border-gray-300'} rounded-2xl p-8 max-w-sm w-full shadow-2xl`}>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5 mx-auto" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}>
              <Trash2 size={20} className="text-red-400" />
            </div>
            <h3 className={`font-sans font-bold text-base ${theme.text.primary} text-center mb-1`}>Delete package?</h3>
            <p className={`text-xs font-sans ${theme.text.muted} text-center mb-6`}>
              <span className="text-red-400 font-semibold">⚠ WARNING:</span> This will permanently remove the package for <span className={`font-medium ${theme.text.secondary}`}>{deleteConfirm.company_name || deleteConfirm.clerk_user_id}</span>. This cannot be undone.
            </p>
            <div className="mb-4">
              <label className={`text-[11px] font-sans ${theme.text.muted} block mb-2`}>
                Type <span className={`font-mono font-bold ${theme.text.secondary}`}>DELETE</span> to confirm
              </label>
              <input
                type="text"
                autoFocus
                value={deleteInput}
                onChange={e => setDeleteInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && deleteInput === 'DELETE') confirmDelete(); if (e.key === 'Escape') { setDeleteConfirm(null); setDeleteInput(''); } }}
                className={`w-full ${theme.input} rounded-lg px-4 py-2.5 text-sm font-mono`}
                placeholder="DELETE"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => { setDeleteConfirm(null); setDeleteInput(''); }}
                className={`flex-1 text-xs font-sans py-2.5 rounded-lg border transition-colors ${isDarkMode ? 'border-slate-600 text-gray-400 hover:border-slate-500' : 'border-gray-300 text-gray-500 hover:border-gray-400'}`}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleteInput !== 'DELETE'}
                className="flex-1 text-xs font-sans font-medium py-2.5 rounded-lg text-white bg-red-500 hover:bg-red-600 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                Delete package
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Active sessions ───────────────────────────────────────────────────────────
const ActiveSessions = ({ user, currentSessionId, isDarkMode, theme }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [revoking, setRevoking] = useState(null);

  useEffect(() => {
    if (!user) return;
    user.getSessions()
      .then(s => {
        console.log('Clerk sessions:', JSON.stringify(s.map(sess => ({
          id: sess.id,
          deviceType: sess.latestActivity?.deviceType,
          browserName: sess.latestActivity?.browserName,
          city: sess.latestActivity?.city,
        })), null, 2));
        setSessions(s);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  // Listen for revoke broadcasts — if this session gets revoked, sign out immediately
  useEffect(() => {
    if (!window.BroadcastChannel) return;
    const bc = new BroadcastChannel('sc_session_revoked');
    bc.onmessage = (e) => {
      if (e.data?.session_id === currentSessionId) {
        window.location.reload();
      }
    };
    return () => bc.close();
  }, [currentSessionId]);

  const revoke = async (session) => {
    setRevoking(session.id);
    try {
      const res = await fetch('/api/clerk-revoke-session.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Key': ADMIN_KEY },
        body: JSON.stringify({ session_id: session.id })
      });
      const data = await res.json();
      if (data.success) {
        setSessions(prev => prev.filter(s => s.id !== session.id));
        // Broadcast to any open tabs with that session
        if (window.BroadcastChannel) {
          const bc = new BroadcastChannel('sc_session_revoked');
          bc.postMessage({ session_id: session.id });
          bc.close();
        }
      } else {
        console.error('Revoke failed:', data.error);
      }
    } catch(e) {
      console.error(e);
    }
    setRevoking(null);
  };

  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('en-GB', {
      timeZone: 'Europe/London',
      day: 'numeric', month: 'short', year: 'numeric'
    }) + ' at ' + new Date(date).toLocaleTimeString('en-GB', {
      timeZone: 'Europe/London',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const isMobile = (deviceType = '', browserName = '') => {
    const d = (deviceType + ' ' + browserName).toLowerCase();
    // Android reports as Linux in Clerk — treat Linux as mobile
    if (deviceType === 'Linux') return true;
    return d.includes('mobile') || d.includes('phone') || d.includes('tablet') || d.includes('android') || d.includes('iphone') || d.includes('ipad');
  };

  return (
    <div className={`${theme.card} border rounded-2xl overflow-hidden mt-4`}>
      <div className={`px-6 py-4 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-gray-200'}`}>
        <h3 className={`font-sans font-semibold text-sm ${theme.text.primary}`}>Active Sessions</h3>
        <p className={`text-[11px] font-sans ${theme.text.muted} mt-0.5`}>Devices currently signed in to your account</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <svg className="animate-spin w-5 h-5 text-pink-400" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
          </svg>
        </div>
      ) : sessions.length === 0 ? (
        <p className={`text-xs font-sans ${theme.text.muted} px-6 py-6 text-center`}>No active sessions found.</p>
      ) : (
        <div>
          {sessions.map((session, idx) => {
            const activity    = session.latestActivity || {};
            const browserName = activity.browserName || '';
            const deviceType  = activity.deviceType || '';
            const city        = activity.city || '';
            const country     = activity.country || '';
            const location    = [city, country].filter(Boolean).join(', ') || 'Unknown location';
            const osLabel     = deviceType === 'Linux' ? 'Android' : (deviceType || '');
            const lastActive  = formatDate(session.lastActiveAt);
            const isCurrent   = session.id === currentSessionId;

            return (
              <div key={session.id} className="flex items-center justify-between px-6 py-4 gap-4" style={idx > 0 ? { borderTop: isDarkMode ? '1px solid rgba(71,85,105,0.3)' : '1px solid rgba(229,231,235,0.8)' } : {}}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-100'}`}>
                    {isMobile(deviceType, browserName) ? (
                      <svg width="18" height="28" viewBox="0 0 18 28" fill="none">
                        <rect x="1" y="1" width="16" height="26" rx="3" fill={isDarkMode ? '#334155' : '#cbd5e1'} stroke={isDarkMode ? '#64748b' : '#94a3b8'} strokeWidth="1"/>
                        <rect x="3" y="4" width="12" height="17" rx="1" fill={isDarkMode ? '#1e293b' : '#f1f5f9'}/>
                        <circle cx="9" cy="24" r="1.2" fill={isDarkMode ? '#64748b' : '#94a3b8'}/>
                        <rect x="6.5" y="2" width="5" height="0.8" rx="0.4" fill={isDarkMode ? '#64748b' : '#94a3b8'}/>
                      </svg>
                    ) : (
                      <svg width="28" height="22" viewBox="0 0 28 22" fill="none">
                        <rect x="3" y="1" width="22" height="15" rx="2" fill={isDarkMode ? '#334155' : '#cbd5e1'} stroke={isDarkMode ? '#64748b' : '#94a3b8'} strokeWidth="1"/>
                        <rect x="5" y="3" width="18" height="11" rx="0.5" fill={isDarkMode ? '#1e293b' : '#f1f5f9'}/>
                        <path d="M1 17h26l-2 3H3l-2-3z" fill={isDarkMode ? '#334155' : '#cbd5e1'} stroke={isDarkMode ? '#64748b' : '#94a3b8'} strokeWidth="1"/>
                        <rect x="10" y="17.5" width="8" height="1" rx="0.5" fill={isDarkMode ? '#64748b' : '#94a3b8'}/>
                      </svg>
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className={`text-xs font-sans font-medium ${theme.text.primary} mb-0.5`}>
                      {[osLabel, browserName].filter(Boolean).join(' · ') || 'Unknown device'}
                    </p>
                    {isCurrent && (
                      <span className="inline-block mb-1 text-[10px] font-sans text-green-400 border border-green-400/20 bg-green-400/10 rounded px-1.5 py-0.5">
                        This device
                      </span>
                    )}
                    <p className={`text-[11px] font-sans ${theme.text.muted}`}>{location}</p>
                    <p className={`text-[11px] font-sans ${theme.text.muted}`}>{lastActive}</p>
                  </div>
                </div>
                {!isCurrent && (
                  <button
                    onClick={() => revoke(session)}
                    disabled={revoking === session.id}
                    className={`flex-shrink-0 text-[11px] font-sans px-3 py-1.5 rounded-lg border transition-colors disabled:opacity-50 ${isDarkMode ? 'border-slate-600 text-gray-400 hover:border-red-400/50 hover:text-red-400' : 'border-gray-300 text-gray-500 hover:border-red-400/50 hover:text-red-400'}`}
                  >
                    {revoking === session.id ? 'Signing out…' : 'Sign out'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── Live hosting stats (WHM) ─────────────────────────────────────────────────
const HostingStats = ({ cpanelUsername, isDarkMode, theme }) => {
  const [stats, setStats]     = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!cpanelUsername) return;
    setLoading(true);
    fetch('/api/cpanel-stats.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Admin-Key': ADMIN_KEY },
      body: JSON.stringify({ cpanel_username: cpanelUsername })
    })
      .then(r => r.json())
      .then(data => { if (data.success) setStats(data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [cpanelUsername]);

  const fmtMB = (mb) => {
    if (!mb || mb === 0) return '0 MB';
    if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
    return `${mb} MB`;
  };
  const pct    = (used, limit) => limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const barCol = (p) => p > 85 ? '#f87171' : p > 65 ? '#fb923c' : '#4ade80';

  if (loading) return (
    <div className="px-6 py-3 flex items-center gap-1.5" style={{ borderTop: isDarkMode ? '1px solid rgba(71,85,105,0.3)' : '1px solid rgba(229,231,235,0.8)' }}>
      <svg className="animate-spin w-3 h-3 text-pink-400 flex-shrink-0" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
      </svg>
      <span className={`text-[10px] font-sans ${theme.text.muted}`}>Loading stats…</span>
    </div>
  );

  if (!stats) return null;

  const chips = [
    stats.domain      && { label: 'Domain',    value: stats.domain,                          colour: null,        span: 2 },
    stats.plan        && { label: 'Plan',       value: stats.plan,                            colour: null,        span: 2 },
    stats.ssl != null && { label: 'SSL',        value: stats.ssl ? 'Active' : 'Inactive',     colour: stats.ssl ? '#4ade80' : '#fb923c', dot: true, span: 1 },
    stats.email_count != null && { label: 'Emails',    value: stats.email_count,              colour: null,        icon: 'email',    span: 1 },
    stats.db_count    != null && { label: 'Databases', value: stats.db_count,                 colour: null,        icon: 'database', span: 1 },
  ].filter(Boolean);

  const hasDisk = stats.disk_limit > 0;
  const hasBw   = stats.bw_limit   > 0;

  return (
    <div className="px-6 py-4" style={{ borderTop: isDarkMode ? '1px solid rgba(71,85,105,0.3)' : '1px solid rgba(229,231,235,0.8)' }}>

      {chips.length > 0 && (
        <div className="space-y-2 mb-3">
          <p className={`text-[9px] font-sans tracking-[0.18em] uppercase ${theme.text.muted}`}>Hosting Information</p>
          {/* Top row — Domain + Plan, 2 cols each */}
          <div className="grid grid-cols-4 gap-2">
            {chips.filter(c => c.span === 2).map(chip => (
              <div
                key={chip.label}
                className="col-span-2 rounded-lg px-2.5 py-2 flex flex-col gap-0.5 min-w-0"
                style={{ background: isDarkMode ? 'rgba(15,23,42,0.5)' : 'rgba(248,250,252,0.9)', border: isDarkMode ? '1px solid rgba(71,85,105,0.25)' : '1px solid rgba(229,231,235,0.9)' }}
              >
                <span className={`text-[8px] font-sans tracking-[0.12em] uppercase ${theme.text.muted} leading-none`}>{chip.label}</span>
                <span className="text-[11px] font-sans font-semibold leading-tight truncate">{chip.value}</span>
              </div>
            ))}
          </div>
          {/* Bottom row — SSL, Emails, Databases, 3 equal cols */}
          <div className="grid grid-cols-3 gap-2">
            {chips.filter(c => c.span === 1).map(chip => (
              <div
                key={chip.label}
                className="rounded-lg px-2.5 py-2 flex flex-col gap-0.5 min-w-0"
                style={{ background: isDarkMode ? 'rgba(15,23,42,0.5)' : 'rgba(248,250,252,0.9)', border: isDarkMode ? '1px solid rgba(71,85,105,0.25)' : '1px solid rgba(229,231,235,0.9)' }}
              >
                <span className={`text-[8px] font-sans tracking-[0.12em] uppercase ${theme.text.muted} leading-none`}>{chip.label}</span>
                <span className="text-[11px] font-sans font-semibold leading-tight truncate flex items-center gap-1" style={{ color: chip.colour || undefined }}>
                  {chip.dot && <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: chip.colour }} />}
                  {chip.icon === 'email' && (
                    <svg width="10" height="10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 opacity-60"><path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
                  )}
                  {chip.icon === 'database' && (
                    <svg width="10" height="10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 opacity-60"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/></svg>
                  )}
                  {chip.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Usage bars */}
      {(hasDisk || hasBw) && (
        <div className="space-y-2">
          {hasDisk && (
            <div className="flex items-center gap-2">
              <span className={`text-[8px] font-sans tracking-[0.1em] uppercase flex-shrink-0 w-8 ${theme.text.muted}`}>Disk</span>
              <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: isDarkMode ? 'rgba(71,85,105,0.4)' : 'rgba(229,231,235,0.8)' }}>
                <div className="h-full rounded-full transition-all" style={{ width: `${pct(stats.disk_used, stats.disk_limit)}%`, background: barCol(pct(stats.disk_used, stats.disk_limit)) }} />
              </div>
              <span className={`text-[8px] font-sans flex-shrink-0 ${theme.text.muted} w-20 text-right`}>{fmtMB(stats.disk_used)}/{fmtMB(stats.disk_limit)}</span>
            </div>
          )}
          {hasBw && (
            <div className="flex items-center gap-2">
              <span className={`text-[8px] font-sans tracking-[0.1em] uppercase flex-shrink-0 w-8 ${theme.text.muted}`}>BW</span>
              <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: isDarkMode ? 'rgba(71,85,105,0.4)' : 'rgba(229,231,235,0.8)' }}>
                <div className="h-full rounded-full transition-all" style={{ width: `${pct(stats.bw_used, stats.bw_limit)}%`, background: barCol(pct(stats.bw_used, stats.bw_limit)) }} />
              </div>
              <span className={`text-[8px] font-sans flex-shrink-0 ${theme.text.muted} w-20 text-right`}>{fmtMB(stats.bw_used)}/{fmtMB(stats.bw_limit)}</span>
            </div>
          )}
        </div>
      )}

    </div>
  );
};

// ── Client management drawer (tabbed) ────────────────────────────────────────
const ClientManageDrawer = ({ client, clerkUser, adminForm, setAdminForm, onSave, onClose, saving, isDarkMode, theme }) => {
  const [activeTab, setActiveTab] = React.useState('package');
  const cpanelUser = client.cpanel_username;

  const tabs = [
    { id: 'package',  label: 'Package',  icon: FileText },
    { id: 'account',  label: 'cPanel',   icon: Activity },
    { id: 'payments', label: 'Invoices',  icon: FileText },
    { id: 'notes',    label: 'Notes',    icon: StickyNote },
  ];

  return (
    <div>
      <div className="flex" style={{ borderBottom: isDarkMode ? '1px solid rgba(71,85,105,0.4)' : '1px solid rgba(229,231,235,0.8)' }}>
        {tabs.map(t => {
          const Icon = t.icon;
          const active = activeTab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-[11px] font-sans font-medium transition-colors relative ${active ? (isDarkMode ? 'text-white' : 'text-slate-900') : theme.text.muted}`}
            >
              <Icon size={12} />
              {t.label}
              {active && <span className="absolute bottom-0 left-0 right-0 h-0.5 rounded-t-full" style={{ background: '#eb008b' }} />}
            </button>
          );
        })}
      </div>
      <div className="px-5 py-5">
        {activeTab === 'package' && adminForm && (
          <AdminPackageForm
            pkg={adminForm}
            onChange={setAdminForm}
            onSave={onSave}
            onCancel={onClose}
            saving={saving}
            isDarkMode={isDarkMode}
            theme={theme}
          />
        )}
        {activeTab === 'account' && (
          <CpanelTools cpanelUser={cpanelUser} client={client} isDarkMode={isDarkMode} theme={theme} />
        )}
        {activeTab === 'payments' && (
          <PaymentsPanel clerkUserId={client.clerk_user_id} companyName={client.company_name} isDarkMode={isDarkMode} theme={theme} />
        )}
        {activeTab === 'notes' && (
          <NotesPanel clerkUserId={client.clerk_user_id} companyName={client.company_name} isDarkMode={isDarkMode} theme={theme} />
        )}
      </div>
    </div>
  );
};

// ── cPanel / WHM tools ────────────────────────────────────────────────────────
const CpanelTools = ({ cpanelUser, client, isDarkMode, theme }) => {
  const [status, setStatus]           = React.useState(null);
  const [actionMsg, setActionMsg]     = React.useState(null);
  const [actioning, setActioning]     = React.useState(false);
  const [newPass, setNewPass]         = React.useState('');
  const [showPass, setShowPass]       = React.useState(false);
  const [passMsg, setPassMsg]         = React.useState(null);
  const [passWorking, setPassWorking] = React.useState(false);

  const callWHM = async (action, extra = {}) => {
    const res = await fetch('/api/whm-api.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Admin-Key': ADMIN_KEY },
      body: JSON.stringify({ action, cpanel_username: cpanelUser, ...extra })
    });
    return res.json();
  };

  const loadStatus = async () => {
    if (!cpanelUser) return;
    setStatus({ loading: true });
    try {
      const data = await callWHM('status');
      if (data.success) setStatus(data);
      else setStatus({ error: data.error });
    } catch { setStatus({ error: 'Failed to connect to WHM' }); }
  };

  React.useEffect(() => { loadStatus(); }, [cpanelUser]);

  const doAction = async (action) => {
    setActioning(true);
    setActionMsg(null);
    try {
      const data = await callWHM(action);
      if (data.success) {
        setActionMsg({ ok: true, text: action === 'suspend' ? 'Account suspended.' : 'Account unsuspended.' });
        loadStatus();
      } else {
        setActionMsg({ ok: false, text: data.error || 'Action failed.' });
      }
    } catch { setActionMsg({ ok: false, text: 'Network error.' }); }
    setActioning(false);
  };

  const changePassword = async () => {
    if (newPass.length < 8) return;
    setPassWorking(true);
    setPassMsg(null);
    try {
      const data = await callWHM('password', { new_password: newPass });
      if (data.success) { setPassMsg({ ok: true, text: 'Password updated.' }); setNewPass(''); }
      else setPassMsg({ ok: false, text: data.error || 'Failed to update password.' });
    } catch { setPassMsg({ ok: false, text: 'Network error.' }); }
    setPassWorking(false);
  };

  if (!cpanelUser) return (
    <div className={`rounded-xl p-5 text-center`} style={{ background: isDarkMode ? 'rgba(15,23,42,0.4)' : 'rgba(248,250,252,0.8)', border: isDarkMode ? '1px solid rgba(71,85,105,0.3)' : '1px solid rgba(229,231,235,0.8)' }}>
      <p className={`text-xs font-sans ${theme.text.muted}`}>No cPanel username set. Add one in the Package tab.</p>
    </div>
  );

  const pct    = (used, limit) => limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const fmtMB  = (mb) => mb >= 1024 ? `${(mb/1024).toFixed(1)} GB` : `${mb} MB`;

  return (
    <div className="space-y-4">
      <div className={`rounded-xl p-4`} style={{ background: isDarkMode ? 'rgba(15,23,42,0.4)' : 'rgba(248,250,252,0.8)', border: isDarkMode ? '1px solid rgba(71,85,105,0.3)' : '1px solid rgba(229,231,235,0.8)' }}>
        <div className="flex items-center justify-between mb-3">
          <p className={`text-[10px] font-sans tracking-[0.15em] uppercase ${theme.text.muted}`}>Account Status</p>
          <button onClick={loadStatus} className={`${theme.text.muted} hover:text-pink-400 transition-colors`}><RefreshCw size={12} /></button>
        </div>
        {status?.loading && (
          <div className="flex items-center gap-2">
            <svg className="animate-spin w-3.5 h-3.5 text-pink-400" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
            <span className={`text-xs font-sans ${theme.text.muted}`}>Fetching from WHM…</span>
          </div>
        )}
        {status?.error && <p className="text-xs font-sans text-red-400">{status.error}</p>}
        {status?.success && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center gap-1.5 text-[10px] font-sans font-semibold px-2.5 py-1 rounded-full ${status.suspended ? 'text-red-400 bg-red-400/10 border border-red-400/20' : 'text-green-400 bg-green-400/10 border border-green-400/20'}`}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: status.suspended ? '#f87171' : '#4ade80' }} />
                  {status.suspended ? 'Suspended' : 'Active'}
                </span>
                <span className={`text-[10px] font-mono ${theme.text.muted}`}>{cpanelUser}</span>
              </div>
              {actionMsg && <span className={`text-[10px] font-sans ${actionMsg.ok ? 'text-green-400' : 'text-red-400'}`}>{actionMsg.text}</span>}
            </div>
            {status.disk_limit > 0 && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-[10px] font-sans flex items-center gap-1 ${theme.text.muted}`}><HardDrive size={10} /> Disk</span>
                  <span className={`text-[10px] font-sans ${theme.text.secondary}`}>{fmtMB(status.disk_used)} / {fmtMB(status.disk_limit)}</span>
                </div>
                <div className="h-1.5 rounded-full overflow-hidden" style={{ background: isDarkMode ? 'rgba(71,85,105,0.4)' : 'rgba(229,231,235,0.8)' }}>
                  <div className="h-full rounded-full" style={{ width: `${pct(status.disk_used, status.disk_limit)}%`, background: pct(status.disk_used, status.disk_limit) > 85 ? '#f87171' : '#eb008b' }} />
                </div>
              </div>
            )}
            {status.bw_limit > 0 && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-[10px] font-sans flex items-center gap-1 ${theme.text.muted}`}><Wifi size={10} /> Bandwidth</span>
                  <span className={`text-[10px] font-sans ${theme.text.secondary}`}>{fmtMB(status.bw_used)} / {fmtMB(status.bw_limit)}</span>
                </div>
                <div className="h-1.5 rounded-full overflow-hidden" style={{ background: isDarkMode ? 'rgba(71,85,105,0.4)' : 'rgba(229,231,235,0.8)' }}>
                  <div className="h-full rounded-full" style={{ width: `${pct(status.bw_used, status.bw_limit)}%`, background: pct(status.bw_used, status.bw_limit) > 85 ? '#f87171' : '#3b82f6' }} />
                </div>
              </div>
            )}
            <div className="flex gap-2 pt-1">
              {!status.suspended ? (
                <button onClick={() => doAction('suspend')} disabled={actioning} className="flex items-center gap-1.5 text-[11px] font-sans font-medium px-3 py-2 rounded-lg border transition-colors disabled:opacity-40 text-red-400 border-red-400/30 hover:bg-red-400/10">
                  <Ban size={11} /> Suspend account
                </button>
              ) : (
                <button onClick={() => doAction('unsuspend')} disabled={actioning} className="flex items-center gap-1.5 text-[11px] font-sans font-medium px-3 py-2 rounded-lg border transition-colors disabled:opacity-40 text-green-400 border-green-400/30 hover:bg-green-400/10">
                  <Unlock size={11} /> Unsuspend account
                </button>
              )}
            </div>
          </div>
        )}
      </div>
      <div className={`rounded-xl p-4`} style={{ background: isDarkMode ? 'rgba(15,23,42,0.4)' : 'rgba(248,250,252,0.8)', border: isDarkMode ? '1px solid rgba(71,85,105,0.3)' : '1px solid rgba(229,231,235,0.8)' }}>
        <p className={`text-[10px] font-sans tracking-[0.15em] uppercase ${theme.text.muted} mb-3`}>Change cPanel Password</p>
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <input type={showPass ? 'text' : 'password'} value={newPass} onChange={e => setNewPass(e.target.value)} onKeyDown={e => e.key === 'Enter' && changePassword()} placeholder="New password (min 8 chars)" autoComplete="new-password" name="cpanel-new-password" className={`w-full ${theme.input} rounded-lg px-3 py-2 text-xs font-sans pr-8`} />
            <button onClick={() => setShowPass(v => !v)} className={`absolute right-2.5 top-1/2 -translate-y-1/2 ${theme.text.muted} hover:text-pink-400 transition-colors`}>
              {showPass ? <EyeOff size={12} /> : <Eye size={12} />}
            </button>
          </div>
          <button onClick={changePassword} disabled={newPass.length < 8 || passWorking} className="flex items-center gap-1.5 text-[11px] font-sans font-medium text-white px-3 py-2 rounded-lg transition-opacity hover:opacity-90 disabled:opacity-40 flex-shrink-0" style={{ background: '#eb008b' }}>
            <Lock size={11} /> {passWorking ? 'Saving…' : 'Set'}
          </button>
        </div>
        {passMsg && <p className={`text-[11px] font-sans mt-2 ${passMsg.ok ? 'text-green-400' : 'text-red-400'}`}>{passMsg.text}</p>}
      </div>
    </div>
  );
};

// ── Payments panel ────────────────────────────────────────────────────────────
// ── Client-facing invoices tab ────────────────────────────────────────────────
const ClientInvoicesTab = ({ clientPackage, packageLoading, userId, isDarkMode, theme, onClose }) => {
  const [customInvoices, setCustomInvoices] = useState([]);
  const [loadingCustom, setLoadingCustom]   = useState(true);
  const [expanded, setExpanded]             = useState(null);

  useEffect(() => {
    if (!userId) return;
    fetch('/api/admin-notes.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Admin-Key': ADMIN_KEY },
      body: JSON.stringify({ action: 'list', clerk_user_id: userId, type: 'payment' })
    })
      .then(r => r.json())
      .then(data => { if (data.success) setCustomInvoices(data.entries); })
      .catch(() => {})
      .finally(() => setLoadingCustom(false));
  }, [userId]);

  if (packageLoading || loadingCustom) return (
    <div className="flex items-center gap-2 py-8">
      <svg className="animate-spin w-4 h-4 text-pink-400" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
      <span className={`text-xs font-sans ${theme.text.muted}`}>Loading invoices…</span>
    </div>
  );

  // Build the auto-generated invoice from the package
  const autoInvoice = clientPackage ? (() => {
    const lines = [
      ...(clientPackage.coreServices || []).filter(s => s.paid !== false && s.price),
      ...(clientPackage.addons || []).filter(a => a.paid && a.price),
    ].map(item => {
      const price = parseFloat(item.price) || 0;
      const discount = parseFloat(item.discount) || 0;
      const net = Math.max(0, price - discount);
      const periodLabel = { month: '/mo', year: '/yr', quarter: '/qtr', week: '/wk', 'one-off': '' }[item.period || 'month'] || '/mo';
      return { label: item.label, price, discount, net, period: item.period || 'month', periodLabel };
    });
    const monthlyTotal = lines.reduce((sum, l) => {
      if (l.period === 'month')   return sum + l.net;
      if (l.period === 'quarter') return sum + (l.net / 3);
      if (l.period === 'week')    return sum + (l.net * 4.33);
      return sum;
    }, 0);
    const yearlyLines  = lines.filter(l => l.period === 'year');
    const oneOffLines  = lines.filter(l => l.period === 'one-off');
    const d = new Date();
    const num = `INV-${d.getFullYear()}${String(d.getMonth()+1).padStart(2,'0')}-001`;
    const due = clientPackage.renewalDate && clientPackage.renewalDate !== 'Monthly rolling'
      ? clientPackage.renewalDate
      : (() => { const n = new Date(); n.setDate(1); n.setMonth(n.getMonth()+1); return n.toLocaleDateString('en-GB',{day:'numeric',month:'long',year:'numeric'}); })();
    return { id: 'auto', num, lines, monthlyTotal, yearlyLines, oneOffLines, due, status: 'upcoming', description: clientPackage.name, companyName: clientPackage.companyName, auto: true };
  })() : null;

  const statusColour = s => {
    if (s === 'paid')     return { text: 'text-green-400',  bg: 'bg-green-400/10',  border: 'border-green-400/20'  };
    if (s === 'overdue')  return { text: 'text-red-400',    bg: 'bg-red-400/10',    border: 'border-red-400/20'    };
    if (s === 'upcoming') return { text: 'text-yellow-400', bg: 'bg-yellow-400/10', border: 'border-yellow-400/20' };
    return                       { text: 'text-yellow-400', bg: 'bg-yellow-400/10', border: 'border-yellow-400/20' };
  };

  const allInvoices = [
    ...(autoInvoice ? [autoInvoice] : []),
    ...customInvoices.map(e => ({ ...e, id: `custom-${e.id}`, dbId: e.id, auto: false })),
  ];

  if (allInvoices.length === 0) return (
    <div className={`${theme.card} border rounded-2xl overflow-hidden`}>
      <div style={{ height: 3, background: 'linear-gradient(to right, #eb008b, transparent)' }} />
      <div className="px-8 py-10 text-center">
        <p className={`font-sans font-black uppercase text-lg ${theme.text.primary}`} style={{ letterSpacing: '0.05em' }}>No invoices yet</p>
        <p className={`text-xs font-sans ${theme.text.muted} mt-1`}>Your billing history will appear here once invoices are issued.</p>
      </div>
    </div>
  );

  return (
    <div className="w-full space-y-3">
      {allInvoices.map(inv => {
        const isExpanded = expanded === inv.id;
        const sc = statusColour(inv.status || 'upcoming');
        const totalLabel = inv.auto
          ? (inv.monthlyTotal > 0 ? `£${inv.monthlyTotal.toFixed(2)}/mo` : inv.yearlyLines?.length > 0 ? `£${inv.yearlyLines.reduce((s,l)=>s+l.net,0).toFixed(2)}/yr` : '—')
          : `£${parseAmount(inv.amount).toFixed(2)}`;

        // PDF download URL
        const pdfUrl = inv.auto
          ? `/api/invoice-pdf.php?type=auto&clerk_user_id=${userId}&admin_key=${ADMIN_KEY}`
          : `/api/invoice-pdf.php?type=custom&id=${inv.dbId}&admin_key=${ADMIN_KEY}`;

        return (
          <div key={inv.id} className={`${theme.card} border rounded-2xl overflow-hidden transition-all`}>
            {/* Row header */}
            <div
              className="flex items-center justify-between px-6 py-4 cursor-pointer"
              onClick={() => setExpanded(isExpanded ? null : inv.id)}
            >
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="#eb008b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                  <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
                <div className="min-w-0">
                  <p className={`text-sm font-sans font-semibold ${theme.text.primary} truncate`}>{inv.auto ? inv.num : inv.description}</p>
                  <p className={`text-[10px] font-sans ${theme.text.muted}`}>{inv.auto ? 'Auto-generated · ' : ''}{inv.due ? `Due ${inv.due}` : inv.due_date ? `Due ${new Date(inv.due_date).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'})}` : ''}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <span className={`text-xs font-sans font-bold ${theme.text.primary}`}>{totalLabel}</span>
                <span className={`hidden sm:inline-flex items-center gap-1 text-[9px] font-sans font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${sc.text} ${sc.bg} ${sc.border}`}>
                  {inv.status || 'upcoming'}
                </span>
                <a
                  href={pdfUrl}
                  target="_blank"
                  rel="noreferrer"
                  onClick={e => e.stopPropagation()}
                  className={`flex items-center gap-1 text-[10px] font-sans ${theme.text.muted} hover:text-pink-400 transition-colors`}
                  title="Download PDF"
                >
                  <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 10v6m0 0l-3-3m3 3l3-3M3 17v3a1 1 0 001 1h16a1 1 0 001-1v-3"/>
                  </svg>
                  PDF
                </a>
                {isExpanded ? <ChevronUp size={14} className={theme.text.muted} /> : <ChevronDown size={14} className={theme.text.muted} />}
              </div>
            </div>

            {/* Expanded detail */}
            {isExpanded && (
              <div style={{ borderTop: isDarkMode ? '1px solid rgba(71,85,105,0.3)' : '1px solid rgba(229,231,235,0.8)' }}>
                {inv.auto ? (
                  <>
                    {/* Auto invoice line items */}
                    {inv.lines.length > 0 && (
                      <div className="px-6 py-4" style={{ borderBottom: isDarkMode ? '1px solid rgba(71,85,105,0.3)' : '1px solid rgba(229,231,235,0.8)' }}>
                        <p className={`text-[9px] font-sans tracking-[0.18em] uppercase ${theme.text.muted} mb-3`}>Services</p>
                        <div className="space-y-2">
                          {inv.lines.map((line, i) => (
                            <div key={i} className="flex items-center justify-between gap-4">
                              <p className={`text-xs font-sans ${theme.text.secondary} flex-1`}>{line.label}</p>
                              <div className="flex items-baseline gap-1">
                                {line.discount > 0 && <span className={`text-[10px] font-sans line-through ${theme.text.muted}`}>£{line.price.toFixed(2)}</span>}
                                <span className={`text-xs font-sans font-semibold ${theme.text.primary}`}>£{line.net.toFixed(2)}</span>
                                <span className={`text-[9px] font-sans ${theme.text.muted}`}>{line.periodLabel}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {inv.monthlyTotal > 0 && (
                      <div className="px-6 py-3 flex items-center justify-between" style={{ background: isDarkMode ? 'rgba(15,23,42,0.3)' : 'rgba(248,250,252,0.6)', borderBottom: isDarkMode ? '1px solid rgba(71,85,105,0.3)' : '1px solid rgba(229,231,235,0.8)' }}>
                        <p className={`text-[9px] font-sans tracking-[0.18em] uppercase ${theme.text.muted}`}>Monthly total</p>
                        <p className={`font-sans font-black text-lg ${theme.text.primary}`}>£{inv.monthlyTotal.toFixed(2)}<span className={`text-[10px] font-normal ${theme.text.muted} ml-1`}>/mo</span></p>
                      </div>
                    )}
                    {inv.yearlyLines?.length > 0 && (
                      <div className="px-6 py-3 flex items-center justify-between" style={{ background: isDarkMode ? 'rgba(15,23,42,0.3)' : 'rgba(248,250,252,0.6)', borderBottom: isDarkMode ? '1px solid rgba(71,85,105,0.3)' : '1px solid rgba(229,231,235,0.8)' }}>
                        <div>
                          <p className={`text-[9px] font-sans tracking-[0.18em] uppercase ${theme.text.muted}`}>Billed annually</p>
                          <p className={`text-[9px] font-sans ${theme.text.muted} mt-0.5`}>{inv.yearlyLines.map(l=>l.label).join(', ')}</p>
                        </div>
                        <p className={`font-sans font-black text-lg ${theme.text.primary}`}>£{inv.yearlyLines.reduce((s,l)=>s+l.net,0).toFixed(2)}<span className={`text-[10px] font-normal ${theme.text.muted} ml-1`}>/yr</span></p>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="px-6 py-4" style={{ borderBottom: isDarkMode ? '1px solid rgba(71,85,105,0.3)' : '1px solid rgba(229,231,235,0.8)' }}>
                    <div className="flex items-center justify-between gap-4 mb-2">
                      <p className={`text-xs font-sans font-semibold ${theme.text.primary}`}>{inv.description}</p>
                      <p className={`text-sm font-sans font-black ${theme.text.primary}`}>£{parseAmount(inv.amount).toFixed(2)}</p>
                    </div>
                    {inv.type && <p className={`text-[10px] font-sans capitalize ${theme.text.muted}`}>{inv.type}</p>}
                  </div>
                )}
                {/* Footer */}
                <div className="px-6 py-3 flex items-center justify-between gap-4">
                  <p className={`text-[11px] font-sans ${theme.text.muted}`}>
                    Questions?{' '}
                    <button onClick={() => { if (typeof onClose==='function') onClose(); setTimeout(()=>document.getElementById('contact')?.scrollIntoView({behavior:'smooth',block:'start'}),500); }} className="text-pink-400 hover:text-pink-300 transition-colors">Contact us</button>
                  </p>
                  <a href={pdfUrl} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-[11px] font-sans font-medium text-white px-3 py-1.5 rounded-lg transition-opacity hover:opacity-90" style={{ background: '#eb008b' }}>
                    <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 10v6m0 0l-3-3m3 3l3-3M3 17v3a1 1 0 001 1h16a1 1 0 001-1v-3"/></svg>
                    Download PDF
                  </a>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ── Admin invoices panel ──────────────────────────────────────────────────────
const PaymentsPanel = ({ clerkUserId, companyName, isDarkMode, theme }) => {
  const [entries, setEntries]       = React.useState([]);
  const [loading, setLoading]       = React.useState(true);
  const [form, setForm]             = React.useState({ type: 'invoice', amount: '', description: '', due_date: '', status: 'unpaid' });
  const [saving, setSaving]         = React.useState(false);
  const [msg, setMsg]               = React.useState(null);
  const [showForm, setShowForm]     = React.useState(false);
  const [editingId, setEditingId]   = React.useState(null);
  const [editForm, setEditForm]     = React.useState(null);
  const [editSaving, setEditSaving] = React.useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/admin-notes.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Key': ADMIN_KEY },
        body: JSON.stringify({ action: 'list', clerk_user_id: clerkUserId, type: 'payment' })
      });
      const data = await res.json();
      if (data.success) setEntries(data.entries);
    } catch {}
    setLoading(false);
  };
  React.useEffect(() => { load(); }, [clerkUserId]);

  const save = async () => {
    if (!form.amount || !form.description) return;
    setSaving(true); setMsg(null);
    try {
      const res = await fetch('/api/admin-notes.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Key': ADMIN_KEY },
        body: JSON.stringify({ action: 'add', clerk_user_id: clerkUserId, record_type: 'payment', ...form })
      });
      const data = await res.json();
      if (data.success) { setMsg({ ok: true, text: 'Invoice added — visible to client.' }); setForm({ type: 'invoice', amount: '', description: '', due_date: '', status: 'unpaid' }); setShowForm(false); load(); }
      else setMsg({ ok: false, text: data.error || 'Error.' });
    } catch { setMsg({ ok: false, text: 'Network error.' }); }
    setSaving(false);
  };

  const startEdit = (e) => {
    setEditingId(e.id);
    setEditForm({ type: e.type || 'invoice', amount: e.amount || '', description: e.description || '', due_date: e.due_date ? e.due_date.split('T')[0].split(' ')[0] : '', status: e.status || 'unpaid' });
  };

  const saveEdit = async () => {
    if (!editForm.amount || !editForm.description) return;
    setEditSaving(true);
    try {
      const res = await fetch('/api/admin-notes.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Key': ADMIN_KEY },
        body: JSON.stringify({ action: 'update', id: editingId, ...editForm })
      });
      const data = await res.json();
      if (data.success) { setEditingId(null); setEditForm(null); load(); }
    } catch {}
    setEditSaving(false);
  };

  const updateStatus = async (id, status) => {
    try {
      await fetch('/api/admin-notes.php', { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Admin-Key': ADMIN_KEY }, body: JSON.stringify({ action: 'update_status', id, status }) });
      load();
    } catch {}
  };

  const deleteEntry = async (id) => {
    try {
      await fetch('/api/admin-notes.php', { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Admin-Key': ADMIN_KEY }, body: JSON.stringify({ action: 'delete', id }) });
      load();
    } catch {}
  };

  const statusCls = s => s === 'paid' ? 'text-green-400 bg-green-400/10 border-green-400/20' : s === 'overdue' ? 'text-red-400 bg-red-400/10 border-red-400/20' : 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20';
  const inputCls  = `w-full ${theme.input} rounded-lg px-3 py-2 text-xs font-sans`;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <p className={`text-xs font-sans font-semibold ${theme.text.primary}`}>Invoices</p>
          <p className={`text-[10px] font-sans ${theme.text.muted} mt-0.5`}>Custom invoices appear in the client's Invoices tab</p>
        </div>
        <button onClick={() => setShowForm(v => !v)} className="flex items-center gap-1.5 text-[11px] font-sans font-medium text-white px-3 py-1.5 rounded-lg" style={{ background: '#eb008b' }}><Plus size={11} /> New invoice</button>
      </div>

      {showForm && (
        <div className={`rounded-xl p-4 space-y-2`} style={{ background: isDarkMode ? 'rgba(15,23,42,0.4)' : 'rgba(248,250,252,0.8)', border: isDarkMode ? '1px solid rgba(71,85,105,0.3)' : '1px solid rgba(229,231,235,0.8)' }}>
          <div className="grid grid-cols-2 gap-2">
            <select className={inputCls} value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
              <option value="invoice">Invoice</option>
              <option value="credit">Credit note</option>
              <option value="refund">Refund</option>
            </select>
            <input className={inputCls} placeholder="0.00 (no £ needed)" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} />
          </div>
          <input className={inputCls} placeholder="Description e.g. Website redesign — June 2026" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={`text-[9px] font-sans tracking-[0.12em] uppercase ${theme.text.muted} block mb-1`}>Due date</label>
              <input className={inputCls} type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} />
            </div>
            <div>
              <label className={`text-[9px] font-sans tracking-[0.12em] uppercase ${theme.text.muted} block mb-1`}>Status</label>
              <select className={inputCls} value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                <option value="unpaid">Unpaid</option>
                <option value="paid">Paid</option>
                <option value="overdue">Overdue</option>
              </select>
            </div>
          </div>
          {msg && <p className={`text-[11px] font-sans ${msg.ok ? 'text-green-400' : 'text-red-400'}`}>{msg.text}</p>}
          <div className="flex gap-2">
            <button onClick={save} disabled={saving || !form.amount || !form.description} className="text-[11px] font-sans font-medium text-white px-3 py-2 rounded-lg disabled:opacity-40" style={{ background: '#eb008b' }}>{saving ? 'Saving…' : 'Add invoice'}</button>
            <button onClick={() => setShowForm(false)} className={`text-[11px] font-sans ${theme.text.muted}`}>Cancel</button>
          </div>
        </div>
      )}

      {loading && <p className={`text-xs font-sans ${theme.text.muted}`}>Loading…</p>}
      {!loading && entries.length === 0 && (
        <p className={`text-xs font-sans ${theme.text.muted}`}>No custom invoices yet. The auto-generated invoice from the package is always shown to the client.</p>
      )}

      <div className="space-y-2">
        {entries.map(e => (
          <div key={e.id} className={`rounded-xl overflow-hidden`} style={{ background: isDarkMode ? 'rgba(15,23,42,0.4)' : 'rgba(248,250,252,0.8)', border: isDarkMode ? '1px solid rgba(71,85,105,0.3)' : '1px solid rgba(229,231,235,0.8)' }}>

            {editingId !== e.id ? (
              <div className="p-3 flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    <span className={`text-xs font-sans font-semibold ${theme.text.primary}`}>£{parseAmount(e.amount).toFixed(2)}</span>
                    <span className={`text-[9px] font-sans font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded border ${statusCls(e.status)}`}>{e.status}</span>
                    <span className={`text-[10px] font-sans capitalize ${theme.text.muted}`}>{e.type}</span>
                  </div>
                  <p className={`text-[11px] font-sans ${theme.text.secondary} truncate`}>{e.description}</p>
                  {e.due_date && <p className={`text-[10px] font-sans ${theme.text.muted} mt-0.5`}>Due: {new Date(e.due_date).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'})}</p>}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {e.status !== 'paid'   && <button onClick={() => updateStatus(e.id, 'paid')}    className="text-[10px] font-sans text-green-400 hover:text-green-300 transition-colors">Paid</button>}
                  {e.status === 'unpaid' && <button onClick={() => updateStatus(e.id, 'overdue')} className="text-[10px] font-sans text-red-400 hover:text-red-300 transition-colors">Overdue</button>}
                  <button onClick={() => startEdit(e)} className={`text-[10px] font-sans ${theme.text.muted} hover:text-pink-400 transition-colors`}>Edit</button>
                  <button onClick={() => deleteEntry(e.id)} className={`${theme.text.muted} hover:text-red-400 transition-colors`}><Trash2 size={11} /></button>
                </div>
              </div>
            ) : (
              editForm && (
                <div className="p-3 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <select className={inputCls} value={editForm.type} onChange={ev => setEditForm(f => ({ ...f, type: ev.target.value }))}>
                      <option value="invoice">Invoice</option>
                      <option value="credit">Credit note</option>
                      <option value="refund">Refund</option>
                    </select>
                    <input className={inputCls} placeholder="0.00 (no £ needed)" value={editForm.amount} onChange={ev => setEditForm(f => ({ ...f, amount: ev.target.value }))} />
                  </div>
                  <input className={inputCls} placeholder="Description" value={editForm.description} onChange={ev => setEditForm(f => ({ ...f, description: ev.target.value }))} />
                  <div className="grid grid-cols-2 gap-2">
                    <input className={inputCls} type="date" value={editForm.due_date} onChange={ev => setEditForm(f => ({ ...f, due_date: ev.target.value }))} />
                    <select className={inputCls} value={editForm.status} onChange={ev => setEditForm(f => ({ ...f, status: ev.target.value }))}>
                      <option value="unpaid">Unpaid</option>
                      <option value="paid">Paid</option>
                      <option value="overdue">Overdue</option>
                    </select>
                  </div>
                  <div className="flex gap-2 pt-1">
                    <button onClick={saveEdit} disabled={editSaving || !editForm.amount || !editForm.description} className="text-[11px] font-sans font-medium text-white px-3 py-1.5 rounded-lg disabled:opacity-40" style={{ background: '#eb008b' }}>{editSaving ? 'Saving…' : 'Save changes'}</button>
                    <button onClick={() => { setEditingId(null); setEditForm(null); }} className={`text-[11px] font-sans ${theme.text.muted}`}>Cancel</button>
                  </div>
                </div>
              )
            )}

          </div>
        ))}
      </div>
    </div>
  );
};

// ── Notes panel ───────────────────────────────────────────────────────────────
const NotesPanel = ({ clerkUserId, companyName, isDarkMode, theme }) => {
  const [notes, setNotes]     = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [text, setText]       = React.useState('');
  const [saving, setSaving]   = React.useState(false);
  const [msg, setMsg]         = React.useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/admin-notes.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Key': ADMIN_KEY },
        body: JSON.stringify({ action: 'list', clerk_user_id: clerkUserId, type: 'note' })
      });
      const data = await res.json();
      if (data.success) setNotes(data.entries);
    } catch {}
    setLoading(false);
  };
  React.useEffect(() => { load(); }, [clerkUserId]);

  const save = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      const res = await fetch('/api/admin-notes.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Key': ADMIN_KEY },
        body: JSON.stringify({ action: 'add', clerk_user_id: clerkUserId, record_type: 'note', description: text })
      });
      const data = await res.json();
      if (data.success) { setText(''); load(); }
      else setMsg({ ok: false, text: data.error || 'Error.' });
    } catch { setMsg({ ok: false, text: 'Network error.' }); }
    setSaving(false);
  };

  const deleteNote = async (id) => {
    try {
      await fetch('/api/admin-notes.php', { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Admin-Key': ADMIN_KEY }, body: JSON.stringify({ action: 'delete', id }) });
      load();
    } catch {}
  };

  return (
    <div className="space-y-3">
      <p className={`text-xs font-sans font-semibold ${theme.text.primary}`}>Internal Notes</p>
      <div className="flex gap-2">
        <textarea value={text} onChange={e => setText(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && e.metaKey) save(); }} placeholder="Add a note… (⌘Enter to save)" rows={2} className={`flex-1 ${theme.input} rounded-lg px-3 py-2 text-xs font-sans resize-none`} />
        <button onClick={save} disabled={saving || !text.trim()} className="flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-lg text-white transition-opacity hover:opacity-90 disabled:opacity-40" style={{ background: '#eb008b' }}>
          <Send size={13} />
        </button>
      </div>
      {msg && <p className={`text-[11px] font-sans ${msg.ok ? 'text-green-400' : 'text-red-400'}`}>{msg.text}</p>}
      {loading && <p className={`text-xs font-sans ${theme.text.muted}`}>Loading…</p>}
      {!loading && notes.length === 0 && <p className={`text-xs font-sans ${theme.text.muted}`}>No notes yet.</p>}
      <div className="space-y-2">
        {notes.map(n => (
          <div key={n.id} className={`rounded-xl p-3 flex items-start gap-3`} style={{ background: isDarkMode ? 'rgba(15,23,42,0.4)' : 'rgba(248,250,252,0.8)', border: isDarkMode ? '1px solid rgba(71,85,105,0.3)' : '1px solid rgba(229,231,235,0.8)' }}>
            <div className="flex-1 min-w-0">
              <p className={`text-xs font-sans ${theme.text.secondary} whitespace-pre-wrap`}>{n.description}</p>
              <p className={`text-[10px] font-sans ${theme.text.muted} mt-1`}>{new Date(n.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} at {new Date(n.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</p>
            </div>
            <button onClick={() => deleteNote(n.id)} className={`flex-shrink-0 ${theme.text.muted} hover:text-red-400 transition-colors`}><Trash2 size={11} /></button>
          </div>
        ))}
      </div>
    </div>
  );
};


// ── Avatar upload ────────────────────────────────────────────────────────────
const AvatarUpload = ({ user, isDarkMode, theme, size = 'sm' }) => {
  const [uploading, setUploading]   = useState(false);
  const [deleting, setDeleting]     = useState(false);
  const [menuOpen, setMenuOpen]     = useState(false);
  const fileRef = React.useRef();
  const avatarSize = size === 'lg' ? 'w-24 h-24' : 'w-16 h-16';
  const rounded    = size === 'lg' ? 'rounded-2xl' : 'rounded-xl';

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setMenuOpen(false);
    setUploading(true);
    try { await user.setProfileImage({ file }); }
    catch (err) { console.error('Failed to update photo:', err); }
    finally { setUploading(false); }
  };

  const handleDelete = async () => {
    setMenuOpen(false);
    setDeleting(true);
    try { await user.setProfileImage({ file: null }); }
    catch (err) { console.error('Failed to remove photo:', err); }
    finally { setDeleting(false); }
  };

  const busy = uploading || deleting;

  return (
    <div className={size === 'lg' ? 'relative flex-shrink-0' : 'flex flex-col items-center gap-1.5 pb-4'} style={size !== 'lg' ? { borderBottom: isDarkMode ? '1px solid rgba(71,85,105,0.3)' : '1px solid rgba(229,231,235,0.8)' } : {}}>
      <div className="relative">
        {user?.imageUrl
          ? <img src={user.imageUrl} alt="" className={`${avatarSize} ${rounded} object-cover transition-opacity ${busy ? 'opacity-40' : 'opacity-100'}`} />
          : <div className={`${avatarSize} ${rounded} flex items-center justify-center text-2xl font-bold text-white transition-opacity ${busy ? 'opacity-40' : 'opacity-100'}`} style={{ background: '#eb008b' }}>{user?.firstName?.[0] ?? '?'}</div>
        }
        {busy && (
          <div className="absolute inset-0 flex items-center justify-center">
            <svg className="animate-spin w-5 h-5 text-pink-400" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
            </svg>
          </div>
        )}

        {/* Pen icon */}
        {!busy && (
          <button
            onClick={() => setMenuOpen(o => !o)}
            className="absolute -bottom-1.5 -right-1.5 w-6 h-6 rounded-lg flex items-center justify-center transition-colors"
            style={{ background: '#eb008b', boxShadow: '0 2px 6px rgba(235,0,139,0.4)' }}
          >
            <svg width="10" height="10" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
          </button>
        )}

        {/* Dropdown menu */}
        {menuOpen && (
          <div
            className="absolute top-full right-0 mt-2 z-10 rounded-xl overflow-hidden shadow-xl"
            style={{ background: isDarkMode ? '#1e293b' : '#fff', border: isDarkMode ? '1px solid rgba(71,85,105,0.5)' : '1px solid rgba(229,231,235,0.9)', minWidth: 140 }}
          >
            <label className={`flex items-center gap-2.5 px-4 py-2.5 text-xs font-sans cursor-pointer transition-colors ${isDarkMode ? 'hover:bg-slate-700/50 text-gray-300' : 'hover:bg-gray-50 text-gray-700'}`}>
              <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
              Upload photo
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} />
            </label>
            {user?.imageUrl && (
              <button
                onClick={handleDelete}
                className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-xs font-sans text-red-400 transition-colors ${isDarkMode ? 'hover:bg-slate-700/50' : 'hover:bg-gray-50'}`}
              >
                <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
                </svg>
                Remove photo
              </button>
            )}
          </div>
        )}
      </div>

      {/* Status label */}
      {busy && (
        <p className="text-[10px] font-sans text-pink-400">{uploading ? 'Uploading…' : 'Removing…'}</p>
      )}
    </div>
  );
};

// ── Reusable package form ────────────────────────────────────────────────────
const AdminPackageForm = ({ pkg, onChange, onSave, onCancel, saving, isDarkMode, theme, isNew, existingIds = [] }) => {
  const { useState: useLocalState, useEffect: useLocalEffect } = React;
  const [clerkUsers, setClerkUsers]       = useLocalState([]);
  const [userSearch, setUserSearch]       = useLocalState('');
  const [dropdownOpen, setDropdownOpen]   = useLocalState(false);
  const [selectedUser, setSelectedUser]   = useLocalState(null);
  const [formTab, setFormTab]             = useLocalState('package');

  useLocalEffect(() => {
    if (!isNew) return;
    fetch('/api/clerk-users.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Admin-Key': ADMIN_KEY },
      body: JSON.stringify({})
    })
      .then(r => r.json())
      .then(data => { if (data.success) setClerkUsers(data.users); })
      .catch(() => {});
  }, [isNew]);

  const filteredUsers = clerkUsers
    .filter(u => !userSearch || u.label.toLowerCase().includes(userSearch.toLowerCase()))
    .sort((a, b) => (a.label || '').localeCompare(b.label || ''));

  const selectUser = (u) => {
    setSelectedUser(u);
    setDropdownOpen(false);
    setUserSearch('');
    onChange(prev => ({
      ...prev,
      clerk_user_id: u.id,
      company_name: prev.company_name || '',
    }));
  };

  const set = (field, val) => onChange(prev => ({ ...prev, [field]: val }));

  const setCore = (i, field, val) => {
    const arr = [...pkg.core_services];
    arr[i] = { ...arr[i], [field]: val };
    onChange(prev => ({ ...prev, core_services: arr }));
  };
  const addCore = () => onChange(prev => ({ ...prev, core_services: [...prev.core_services, { id: 'svc' + Date.now(), label: '', detail: '', paid: true }] }));
  const removeCore = i => onChange(prev => ({ ...prev, core_services: prev.core_services.filter((_, idx) => idx !== i) }));

  const setAddon = (i, field, val) => {
    const arr = [...pkg.addons];
    arr[i] = { ...arr[i], [field]: val };
    onChange(prev => ({ ...prev, addons: arr }));
  };
  const addAddon = () => onChange(prev => ({ ...prev, addons: [...prev.addons, { id: 'addon' + Date.now(), label: '', detail: '', paid: false }] }));
  const removeAddon = i => onChange(prev => ({ ...prev, addons: prev.addons.filter((_, idx) => idx !== i) }));

  const inputCls = `w-full ${theme.input} rounded-lg px-3 py-2 text-xs font-sans`;
  const labelCls = `text-[10px] font-sans tracking-[0.15em] uppercase ${theme.text.muted} block mb-1`;
  const sectionCls = `text-[10px] font-sans tracking-[0.2em] uppercase ${theme.text.muted} mb-3 mt-5`;

  const formTabs = [
    { id: 'package', label: 'Package & Services' },
    { id: 'hosting', label: 'Hosting & cPanel' },
  ];

  return (
    <div className="space-y-3">

      {/* Inner tab bar */}
      <div className="flex mb-1" style={{ borderBottom: isDarkMode ? '1px solid rgba(71,85,105,0.3)' : '1px solid rgba(229,231,235,0.8)' }}>
        {formTabs.map(t => {
          const active = formTab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setFormTab(t.id)}
              className={`px-3 py-2 text-[11px] font-sans font-medium transition-colors relative ${active ? (isDarkMode ? 'text-white' : 'text-slate-900') : theme.text.muted}`}
            >
              {t.label}
              {active && <span className="absolute bottom-0 left-0 right-0 h-0.5 rounded-t-full" style={{ background: '#eb008b' }} />}
            </button>
          );
        })}
      </div>

      {/* ── Package & Services tab ── */}
      {formTab === 'package' && (
        <div className="space-y-3">

          {/* User picker (new clients only) */}
          {isNew && (
            <div>
              <label className={labelCls}>Select user</label>
              <div className="relative">
                <div
                  className={`${inputCls} flex items-center gap-2 cursor-pointer`}
                  onClick={() => setDropdownOpen(o => !o)}
                >
                  {selectedUser ? (
                    <>
                      {selectedUser.avatar && <img src={selectedUser.avatar} alt="" className="w-5 h-5 rounded-md object-cover flex-shrink-0" />}
                      <span className="flex-1 truncate">{selectedUser.label}</span>
                      <button
                        onClick={e => { e.stopPropagation(); setSelectedUser(null); onChange(prev => ({ ...prev, clerk_user_id: '' })); }}
                        className={`${theme.text.muted} hover:text-red-400 text-base leading-none flex-shrink-0`}
                      >×</button>
                    </>
                  ) : (
                    <span className={theme.text.muted}>
                      {clerkUsers.length === 0 ? 'Loading users…' : 'Search or select a user…'}
                    </span>
                  )}
                </div>
                {dropdownOpen && clerkUsers.length > 0 && (
                  <div
                    className={`absolute z-50 w-full mt-1 rounded-xl overflow-hidden shadow-xl`}
                    style={{ background: isDarkMode ? '#1e293b' : '#fff', border: isDarkMode ? '1px solid rgba(71,85,105,0.5)' : '1px solid rgba(229,231,235,0.9)' }}
                  >
                    <div className="p-2 border-b" style={{ borderColor: isDarkMode ? 'rgba(71,85,105,0.3)' : 'rgba(229,231,235,0.8)' }}>
                      <input
                        autoFocus
                        type="text"
                        placeholder="Search by name or email…"
                        value={userSearch}
                        onChange={e => setUserSearch(e.target.value)}
                        className={`w-full bg-transparent text-xs font-sans outline-none ${theme.text.primary} placeholder:${theme.text.muted}`}
                        onClick={e => e.stopPropagation()}
                      />
                    </div>
                    <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                      {filteredUsers.length === 0 ? (
                        <p className={`text-xs font-sans ${theme.text.muted} px-3 py-3`}>No users found</p>
                      ) : filteredUsers.map(u => {
                        const hasPackage = existingIds.includes(u.id);
                        return (
                          <div
                            key={u.id}
                            onClick={() => !hasPackage && selectUser(u)}
                            className={`flex items-center gap-2.5 px-3 py-2.5 transition-colors ${hasPackage ? 'cursor-not-allowed opacity-40' : `cursor-pointer ${isDarkMode ? 'hover:bg-slate-700/50' : 'hover:bg-gray-50'}`}`}
                          >
                            {u.avatar
                              ? <img src={u.avatar} alt="" className="w-6 h-6 rounded-lg object-cover flex-shrink-0" />
                              : <div className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 text-[10px] font-bold text-white" style={{ background: '#eb008b' }}>{(u.name || u.email)[0].toUpperCase()}</div>
                            }
                            <div className="min-w-0 flex-1">
                              {u.name && <p className={`text-xs font-sans font-medium ${theme.text.primary} truncate`}>{u.name}</p>}
                              <p className={`text-[11px] font-sans ${theme.text.muted} truncate`}>{u.email}</p>
                            </div>
                            {hasPackage && <span className={`text-[10px] font-sans ${theme.text.muted} flex-shrink-0`}>configured</span>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              {selectedUser && (
                <p className={`text-[10px] font-mono ${theme.text.muted} mt-1.5`}>{selectedUser.id}</p>
              )}
            </div>
          )}

          {!isNew && (
            <p className={`text-[10px] font-sans ${theme.text.muted} font-mono`}>{pkg.clerk_user_id}</p>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Company name</label>
              <input className={inputCls} placeholder="Acme Ltd" value={pkg.company_name} onChange={e => set('company_name', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Package name</label>
              <input className={inputCls} placeholder="Web Presence" value={pkg.package_name} onChange={e => set('package_name', e.target.value)} />
            </div>
            <div className="sm:col-span-2 grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Since date</label>
                <input className={inputCls} placeholder="June 2025" value={pkg.since_date} onChange={e => set('since_date', e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Renewal date</label>
                <input
                  className={inputCls}
                  placeholder="1 June 2026"
                  value={pkg.monthly_rolling || pkg.renewal_date === 'Monthly rolling' ? 'Monthly rolling' : pkg.renewal_date}
                  disabled={pkg.monthly_rolling || pkg.renewal_date === 'Monthly rolling'}
                  onChange={e => set('renewal_date', e.target.value)}
                />
              </div>
            </div>
            <div className="sm:col-span-2">
              <label className={`flex items-center gap-2 cursor-pointer text-[11px] font-sans ${theme.text.muted}`}>
                <input
                  type="checkbox"
                  checked={pkg.monthly_rolling || pkg.renewal_date === 'Monthly rolling' || false}
                  onChange={e => {
                    set('monthly_rolling', e.target.checked);
                    if (e.target.checked) set('renewal_date', 'Monthly rolling');
                    else set('renewal_date', '');
                  }}
                />
                Monthly rolling contract — no fixed renewal date
              </label>
            </div>
          </div>

          {/* Core services */}
          <p className={sectionCls}>Core services</p>
          {pkg.core_services.map((svc, i) => (
            <div key={i} className="space-y-1.5 mb-3 pb-3" style={i < pkg.core_services.length - 1 ? { borderBottom: isDarkMode ? '1px solid rgba(71,85,105,0.2)' : '1px solid rgba(229,231,235,0.6)' } : {}}>
              <div className="grid grid-cols-[1fr_56px_24px] gap-2 items-center">
                <input className={inputCls} placeholder="Label" value={svc.label} onChange={e => setCore(i, 'label', e.target.value)} />
                <label className={`flex items-center gap-1.5 text-[11px] font-sans ${theme.text.muted} cursor-pointer`}>
                  <input type="checkbox" checked={svc.paid !== false} onChange={e => setCore(i, 'paid', e.target.checked)} />
                  Paid
                </label>
                <button onClick={() => removeCore(i)} className={`${theme.text.muted} hover:text-red-400 transition-colors text-lg leading-none`}>×</button>
              </div>
              <textarea
                className={`${inputCls} resize-y`}
                placeholder={"Detail — use '- ' at start of line for bullets\nE.g.:\n- Unlimited bandwidth\n- 99.9% uptime SLA"}
                rows={3}
                value={svc.detail}
                onChange={e => setCore(i, 'detail', e.target.value)}
              />
              {svc.paid !== false && (
                <div className="grid grid-cols-[100px_1fr] gap-2">
                  <input className={inputCls} placeholder="£0.00" value={svc.price || ''} onChange={e => setCore(i, 'price', e.target.value)} />
                  <select className={inputCls} value={svc.period || 'month'} onChange={e => setCore(i, 'period', e.target.value)}>
                    <option value="month">per month</option>
                    <option value="year">per year</option>
                    <option value="quarter">per quarter</option>
                    <option value="week">per week</option>
                    <option value="one-off">one-off</option>
                  </select>
                </div>
              )}
              {svc.paid !== false && svc.price && (
                <div className="grid grid-cols-[100px_1fr] gap-2">
                  <input className={inputCls} placeholder="Disc. £" value={svc.discount || ''} onChange={e => setCore(i, 'discount', e.target.value)} />
                  <input className={inputCls} placeholder="Discount label e.g. 10% loyalty discount" value={svc.discountLabel || ''} onChange={e => setCore(i, 'discountLabel', e.target.value)} />
                </div>
              )}
            </div>
          ))}
          <button onClick={addCore} className={`text-[11px] font-sans ${theme.text.muted} hover:text-pink-400 transition-colors`}>+ Add service</button>

          {/* Add-ons */}
          <p className={sectionCls}>Add-ons</p>
          {pkg.addons.map((addon, i) => (
            <div key={i} className="space-y-1.5 mb-3 pb-3" style={i < pkg.addons.length - 1 ? { borderBottom: isDarkMode ? '1px solid rgba(71,85,105,0.2)' : '1px solid rgba(229,231,235,0.6)' } : {}}>
              <div className="grid grid-cols-[1fr_56px_24px] gap-2 items-center">
                <input className={inputCls} placeholder="Label" value={addon.label} onChange={e => setAddon(i, 'label', e.target.value)} />
                <label className={`flex items-center gap-1.5 text-[11px] font-sans ${theme.text.muted} cursor-pointer`}>
                  <input type="checkbox" checked={addon.paid} onChange={e => setAddon(i, 'paid', e.target.checked)} />
                  Paid
                </label>
                <button onClick={() => removeAddon(i)} className={`${theme.text.muted} hover:text-red-400 transition-colors text-lg leading-none`}>×</button>
              </div>
              <textarea
                className={`${inputCls} resize-y`}
                placeholder={"Detail — use '- ' at start of line for bullets\nE.g.:\n- Unlimited updates\n- Same day turnaround"}
                rows={3}
                value={addon.detail}
                onChange={e => setAddon(i, 'detail', e.target.value)}
              />
              {addon.paid && (
                <div className="grid grid-cols-[100px_1fr] gap-2">
                  <input className={inputCls} placeholder="£0.00" value={addon.price || ''} onChange={e => setAddon(i, 'price', e.target.value)} />
                  <select className={inputCls} value={addon.period || 'month'} onChange={e => setAddon(i, 'period', e.target.value)}>
                    <option value="month">per month</option>
                    <option value="year">per year</option>
                    <option value="quarter">per quarter</option>
                    <option value="week">per week</option>
                    <option value="one-off">one-off</option>
                  </select>
                </div>
              )}
              {addon.paid && addon.price && (
                <div className="grid grid-cols-[100px_1fr] gap-2">
                  <input className={inputCls} placeholder="Disc. £" value={addon.discount || ''} onChange={e => setAddon(i, 'discount', e.target.value)} />
                  <input className={inputCls} placeholder="Discount label e.g. 10% loyalty discount" value={addon.discountLabel || ''} onChange={e => setAddon(i, 'discountLabel', e.target.value)} />
                </div>
              )}
            </div>
          ))}
          <button onClick={addAddon} className={`text-[11px] font-sans ${theme.text.muted} hover:text-pink-400 transition-colors`}>+ Add add-on</button>

        </div>
      )}

      {/* ── Hosting & cPanel tab ── */}
      {formTab === 'hosting' && (
        <div className="space-y-3">

          <p className={sectionCls} style={{ marginTop: 4 }}>cPanel Access</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>cPanel URL</label>
              <input className={inputCls} placeholder="https://cpanel.example.com" value={pkg.cpanel_url || ''} onChange={e => set('cpanel_url', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Username</label>
              <input className={inputCls} placeholder="username" value={pkg.cpanel_username || ''} onChange={e => set('cpanel_username', e.target.value)} />
            </div>
          </div>

          <p className={sectionCls}>Hosting Details</p>
          <p className={`text-[10px] font-sans ${theme.text.muted} -mt-2 mb-2`}>Shown on the client's dashboard. Leave blank to hide any field.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Server Location</label>
              <input className={inputCls} placeholder="London, UK" value={pkg.server_location || ''} onChange={e => set('server_location', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>SSL Status</label>
              <input className={inputCls} placeholder="Active" value={pkg.ssl_status || ''} onChange={e => set('ssl_status', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Uptime Guarantee</label>
              <input className={inputCls} placeholder="99.9%" value={pkg.uptime_guarantee || ''} onChange={e => set('uptime_guarantee', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Backup Schedule</label>
              <input className={inputCls} placeholder="Daily" value={pkg.backup_schedule || ''} onChange={e => set('backup_schedule', e.target.value)} />
            </div>
          </div>

        </div>
      )}

      {/* Actions — always visible */}
      <div className="flex items-center gap-3 pt-3" style={{ borderTop: isDarkMode ? '1px solid rgba(71,85,105,0.2)' : '1px solid rgba(229,231,235,0.6)', marginTop: 8 }}>
        <button
          onClick={onSave}
          disabled={saving}
          className="text-xs font-sans font-medium text-white px-4 py-2 rounded-lg transition-opacity hover:opacity-90 disabled:opacity-50"
          style={{ background: '#eb008b' }}
        >
          {saving ? 'Saving…' : 'Save package'}
        </button>
        <button onClick={onCancel} className={`text-xs font-sans ${theme.text.muted} hover:${theme.text.secondary} transition-colors`}>
          Cancel
        </button>
      </div>
    </div>
  );
};